import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Toast from '../Toast';
import './BAIQL.css';
import { registerBaiqlLanguage } from '../../utils/baiqlLanguage';
import { registerBaiqlAutocomplete } from '../../utils/baiqlAutocomplete';
import BAIQLTemplateGallery from '../BAIQLTemplateGallery';
import MiniBAIQL from './MiniBAIQL';
import { validateBaiql, debounce as debounceFn } from '../../utils/baiqlValidator';
import { createScript, listScripts, getScript, updateScript, getScriptMeta, updateScriptMeta, deleteScript } from '../../services/baiqlScriptService';
import { uploadsEnabled, createJob as apiCreateJob, uploadFiles as apiUploadFiles, createFolder as apiCreateFolder } from '../../services/storageService';
import authService from '../../services/authService';
import PromptGeneratorModal from '../PromptGeneratorModal';
import { useTier } from '../../hooks/useTier';
import BAIQLCommandPanel from './BAIQLCommandPanel';
const MonacoEditor = React.lazy(() => import('@monaco-editor/react').then(m => ({ default: m.default })).catch(() => ({ default: null })));

// No default template; persist last script

const panelStyles = {
  // Use a fixed height so Monaco's height:100% can resolve correctly
  container: { display: 'flex', gap: 12, height: '72vh' },
  left: { flex: 7, minWidth: 0, display: 'flex', flexDirection: 'column' },
  right: { flex: 3, minWidth: 0, display: 'flex', flexDirection: 'column' },
  card: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  output: { flex: 1, overflow: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 13, whiteSpace: 'pre-wrap' }
};

// Use shared registration from utils

const BAIQLEditor = () => {
  const [code, setCode] = useState(() => {
    try { return localStorage.getItem('baiql_last') || ''; } catch { return ''; }
  });
  const [output, setOutput] = useState('');
  const [status, setStatus] = useState('');
  const [errors, setErrors] = useState([]);
  const [batchType, setBatchType] = useState(null);
  const [scriptId, setScriptId] = useState(null);
  const [scriptName, setScriptName] = useState('Untitled');
  const [lastSaved, setLastSaved] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [showLoad, setShowLoad] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [scriptList, setScriptList] = useState([]);
  const [loadQuery, setLoadQuery] = useState('');
  const [loadSort, setLoadSort] = useState('modified_desc'); // name_asc|name_desc|modified_asc|modified_desc
  const [showTemplates, setShowTemplates] = useState(false);
  const [showMeta, setShowMeta] = useState(false);
  const [meta, setMeta] = useState({ version:'', author:'', description:'', changelog:'', sharedWith:'', approvalRequiredFrom:'', inheritFrom:'', emitMetaAsTags:false, lockEditsWithoutApproval:false });
  const [agentRunning, setAgentRunning] = useState(null); // null = checking, true = running, false = not running
  const [agentPaired, setAgentPaired] = useState(null); // null = checking, true = paired, false = not paired
  const [showPairingModal, setShowPairingModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [pairingCode, setPairingCode] = useState(null);
  const [pending, setPending] = useState({ save:false, validate:false, execute:false });
  const [toast, setToast] = useState({ show:false, message:'', type:'success' });
  const [showConfirmNew, setShowConfirmNew] = useState(false);
  const [showSaveAsModal, setShowSaveAsModal] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [manageId, setManageId] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPromptGeneratorModal, setShowPromptGeneratorModal] = useState(false);
  const [promptLineInfo, setPromptLineInfo] = useState(null);
  const navigate = useNavigate();
  const [isPro, setIsPro] = useState(false);
  const location = useLocation();
  const { tierInfo, canAccessFeature, getFeatureDenialMessage } = useTier();
  const [uiMode, setUiMode] = useState(() => {
    try { return localStorage.getItem('baiql_mode') || 'Basic'; } catch { return 'Basic'; }
  });
  const [openMenu, setOpenMenu] = useState(null); // 'file' | 'edit' | 'mode' | 'run' | null
  const [showShortcuts, setShowShortcuts] = useState(false);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const fileOpenRef = useRef(null);
  const promptCommandHandlerRef = useRef(null);
  const hoverPositionRef = useRef(null);
  const modalOpenRef = useRef(false);
  const inputUploadInRef = useRef(null);
  // SCREEN modal state
  const [screenState, setScreenState] = useState({ show:false, execId:null, file:'', format:'txt', content:'', ready:false, saving:false });
  const screenPollerRef = useRef(null);

  // Mini‑BAIQL state lifted to parent
  const miniDefaults = useMemo(() => ({
    selected: 'load',
    pathIn: '', pathInGlobal: false, batchType: '',
    pathLines: '', extLines: '', pathFile: '', extFile: '', pathDir: '', extDir: '', pathRef: '', extRef: '', pathWork: '', extWork: '',
    model: '', modelGlobal: false, modelTemp: 0.7, modelTopP: 1.0,
    prompt: '', promptOptimize: false,
    outFile: '', pathOut: '', pathOutGlobal: false, outFmt: '', printFormat: '', xact: false,
    // LIMIT defaults (persisted in miniState/localStorage)
    limitEnabled: false, limitGlobal: false, limitValue: 1
  }), []);

  const loadMiniState = useCallback(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('mini_baiql_state_v1') || '{}');
      return { ...miniDefaults, ...(saved && typeof saved === 'object' ? saved : {}) };
    } catch { return { ...miniDefaults }; }
  }, [miniDefaults]);

  const [miniState, setMiniState] = useState(loadMiniState);

  const beforeMount = useCallback((monaco) => {
    registerBaiqlLanguage(monaco);
    registerBaiqlAutocomplete(monaco, () => code);
  }, []);

  // Parse PROMPT line to detect structure
  const parsePromptLine = useCallback((lineText, cursorColumn) => {
    // Find PROMPT keyword (case-insensitive, but BAIQL uses uppercase)
    // Handle case where user might have accidentally put quotes around PROMPT itself
    let promptMatch = lineText.match(/\bPROMPT\b/i);
    let hasQuotesAroundPrompt = false;
    
    // Check if PROMPT itself is quoted (user error - we'll remove these)
    if (!promptMatch) {
      const quotedPromptMatch = lineText.match(/["']PROMPT["']/i);
      if (quotedPromptMatch) {
        promptMatch = { index: quotedPromptMatch.index + 1, length: 6 }; // Skip the quote
        hasQuotesAroundPrompt = true;
      } else {
        return null;
      }
    }
    
    const promptStartCol = promptMatch.index + 1; // Monaco is 1-indexed
    const promptEndCol = promptStartCol + 6; // "PROMPT" is 6 characters
    
    // Check if cursor is directly on PROMPT word (accounting for possible quotes around PROMPT)
    const cursorOnPrompt = cursorColumn >= promptStartCol && cursorColumn <= promptEndCol;
    if (!cursorOnPrompt) {
      // If PROMPT has quotes around it, allow cursor to be on the quotes too
      if (hasQuotesAroundPrompt && cursorColumn >= promptStartCol - 1 && cursorColumn <= promptEndCol + 1) {
        // Cursor is on quotes around PROMPT - still valid
      } else {
        return null;
      }
    }
    
    // Find text after PROMPT (skip any quotes around PROMPT itself)
    let afterPromptStart = promptMatch.index + 6;
    if (hasQuotesAroundPrompt) {
      // Skip closing quote if present
      if (lineText[afterPromptStart] === '"' || lineText[afterPromptStart] === "'") {
        afterPromptStart++;
      }
    }
    const afterPrompt = lineText.substring(afterPromptStart).trim();
    
    // Check for quotes around prompt text (single or double)
    let hasQuotes = false;
    let existingText = '';
    let quoteStartCol = null;
    let quoteEndCol = null;
    let options = '';
    
    // Try double quotes first
    const doubleQuoteMatch = afterPrompt.match(/^"([^"]*)"(.*)$/);
    if (doubleQuoteMatch) {
      hasQuotes = true;
      existingText = doubleQuoteMatch[1];
      const quotePos = afterPrompt.indexOf('"');
      quoteStartCol = afterPromptStart + quotePos + 1;
      quoteEndCol = quoteStartCol + existingText.length + 1; // +1 for closing quote
      options = doubleQuoteMatch[2].trim();
    } else {
      // Try single quotes
      const singleQuoteMatch = afterPrompt.match(/^'([^']*)'(.*)$/);
      if (singleQuoteMatch) {
        hasQuotes = true;
        existingText = singleQuoteMatch[1];
        const quotePos = afterPrompt.indexOf("'");
        quoteStartCol = afterPromptStart + quotePos + 1;
        quoteEndCol = quoteStartCol + existingText.length + 1;
        options = singleQuoteMatch[2].trim();
      } else {
        // No quotes - check if there's any text (might be unquoted)
        const textMatch = afterPrompt.match(/^(.+?)(\s+(OPTIMIZE|FORMAT|MATCH|AGGREGATE|SEPARATE|SCREEN|CACHE|NOCACHE|TEMP|TOP_P|XACT|UNTIL|WHILE|BREAK|SKIP|RETRY|HALT|TRACK).*)?$/i);
        if (textMatch) {
          existingText = textMatch[1].trim();
          options = (textMatch[2] || '').trim();
        }
      }
    }
    
    return {
      promptStartCol,
      promptEndCol,
      hasQuotes,
      existingText,
      options,
      quoteStartCol,
      quoteEndCol,
      hasQuotesAroundPrompt
    };
  }, []);

  const onMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    try { monaco.editor.setTheme('baiql-dark'); } catch {}
    
    // Store command handler in ref for access from code action
    // Only opens modal if it's not already open (prevents F8 from toggling)
    promptCommandHandlerRef.current = (lineNumber, promptInfo) => {
      // Don't open if modal is already open (prevents F8 from toggling)
      if (modalOpenRef.current) return;
      setPromptLineInfo({ lineNumber, ...promptInfo });
      setShowPromptGeneratorModal(true);
      modalOpenRef.current = true;
    };
    
    // Register command handler for hover link clicks
    const commandId = 'baiql.openPromptGenerator';
    
    // Create a stable command handler that can be called from Monaco's command system
    // Use a closure to capture editor and refs
    const commandHandler = () => {
      try {
        // Get editor from ref (more reliable than closure)
        const currentEditor = editorRef.current;
        if (!currentEditor) {
          console.warn('Editor not available');
          return;
        }
        
        // Use stored hover position or current editor position
        const position = hoverPositionRef.current || currentEditor.getPosition();
        if (!position) {
          console.warn('No position available');
          return;
        }
        
        const lineNumber = position.lineNumber;
        const model = currentEditor.getModel();
        if (!model) {
          console.warn('Model not available');
          return;
        }
        
        const lineText = model.getLineContent(lineNumber);
        const cursorColumn = position.column;
        
        const promptInfo = parsePromptLine(lineText, cursorColumn);
        if (promptInfo && promptCommandHandlerRef.current) {
          promptCommandHandlerRef.current(lineNumber, promptInfo);
        } else {
          console.warn('No prompt info or handler available', { promptInfo, hasHandler: !!promptCommandHandlerRef.current });
        }
      } catch (error) {
        console.error('Error executing prompt generator command:', error);
        // Don't rethrow - just log the error
      } finally {
        // Clear stored position
        hoverPositionRef.current = null;
      }
    };
    
    // Register command for markdown command links in hover tooltips
    // Try multiple registration methods to ensure it works
    let commandRegistered = false;
    
    // Method 1: Try editor's command service (most reliable for command links)
    if (editor._commandService && typeof editor._commandService.addCommand === 'function') {
      try {
        editor._commandService.addCommand({
          id: commandId,
          handler: commandHandler
        });
        commandRegistered = true;
      } catch (e) {
        console.warn('Failed to register via command service:', e);
      }
    }
    
    // Method 2: Try editor.addCommand (for keybindings, but might work for command links too)
    if (!commandRegistered) {
      try {
        // Use F19 keycode (0x82) as a dummy keybinding
        editor.addCommand(0x82, commandHandler);
        commandRegistered = true;
      } catch (e) {
        console.warn('Failed to register via editor.addCommand:', e);
      }
    }
    
    // Method 3: Try Monaco's global addCommand if available
    if (!commandRegistered && typeof monaco.editor.addCommand === 'function') {
      try {
        monaco.editor.addCommand(0x82, commandId, commandHandler);
        commandRegistered = true;
      } catch (e) {
        console.warn('Failed to register via monaco.editor.addCommand:', e);
      }
    }
    
    if (!commandRegistered) {
      console.warn('Could not register command - command link may not work');
    }
    
    // Register F8 keyboard shortcut to open Prompt Generator when cursor/selection is on PROMPT
    editor.addCommand(monaco.KeyCode.F8, () => {
      // Don't open if modal is already open (check via ref since state isn't accessible in callback)
      if (modalOpenRef.current) return;
      
      const position = editor.getPosition();
      if (!position) return;
      
      const lineNumber = position.lineNumber;
      const model = editor.getModel();
      if (!model) return;
      
      const lineText = model.getLineContent(lineNumber);
      const cursorColumn = position.column;
      
      // Check if cursor/selection is on PROMPT
      const promptMatch = lineText.match(/\bPROMPT\b/i);
      if (!promptMatch) return;
      
      const promptStartCol = promptMatch.index + 1;
      const promptEndCol = promptStartCol + 6;
      
      // Check if cursor is on or near PROMPT
      const selection = editor.getSelection();
      const rangeStart = selection.startColumn;
      const rangeEnd = selection.endColumn;
      const overlapsPrompt = (rangeStart <= promptEndCol && rangeEnd >= promptStartCol) || 
                             (cursorColumn >= promptStartCol && cursorColumn <= promptEndCol);
      
      if (!overlapsPrompt) return;
      
      // Parse prompt info and open modal
      const promptInfo = parsePromptLine(lineText, cursorColumn);
      if (promptInfo && promptCommandHandlerRef.current) {
        promptCommandHandlerRef.current(lineNumber, promptInfo);
      } else {
        // If parsePromptLine fails, create basic info
        const afterPrompt = lineText.substring(promptMatch.index + 6).trim();
        const doubleQuoteMatch = afterPrompt.match(/^"([^"]*)"(.*)$/);
        const singleQuoteMatch = !doubleQuoteMatch ? afterPrompt.match(/^'([^']*)'(.*)$/) : null;
        
        const basicPromptInfo = {
          promptStartCol,
          promptEndCol,
          hasQuotes: !!(doubleQuoteMatch || singleQuoteMatch),
          existingText: doubleQuoteMatch ? doubleQuoteMatch[1] : (singleQuoteMatch ? singleQuoteMatch[1] : ''),
          options: doubleQuoteMatch ? doubleQuoteMatch[2].trim() : (singleQuoteMatch ? singleQuoteMatch[2].trim() : ''),
          quoteStartCol: null,
          quoteEndCol: null,
          hasQuotesAroundPrompt: false
        };
        
        if (promptCommandHandlerRef.current) {
          promptCommandHandlerRef.current(lineNumber, basicPromptInfo);
        }
      }
    });
    
    // Register enhanced hover provider for PROMPT (shows clickable link)
    // This provider runs first and only handles PROMPT, letting others handle everything else
    monaco.languages.registerHoverProvider('baiql', {
      provideHover: (model, position) => {
        const lineNumber = position.lineNumber;
        const lineText = model.getLineContent(lineNumber);
        const cursorColumn = position.column;
        const promptInfo = parsePromptLine(lineText, cursorColumn);
        
        if (promptInfo) {
          // Store position for command execution
          hoverPositionRef.current = position;
          
          // Show enhanced hover with clickable action for PROMPT
          // This will be the only tooltip shown for PROMPT
          return {
            range: {
              startLineNumber: lineNumber,
              startColumn: promptInfo.promptStartCol,
              endLineNumber: lineNumber,
              endColumn: promptInfo.promptEndCol
            },
            contents: [
              { value: '**PROMPT Command**\n\nPROMPT "text with {variables}" [OPTIMIZE]. Sets the model prompt.' },
              { 
                value: '\n💡 Press **F8** while cursor is on PROMPT to open Prompt Generator',
                isTrusted: false
              }
            ]
          };
        }
        
        // Return undefined (not null) to let other hover providers handle non-PROMPT tokens
        // Returning undefined means "I don't handle this, try other providers"
        return undefined;
      }
    });
    
    // Register Code Action provider for PROMPT command (shows lightbulb on Ctrl+.)
    monaco.languages.registerCodeActionProvider('baiql', {
      provideCodeActions: (model, range, context, token) => {
        try {
          const lineNumber = range.startLineNumber;
          const lineText = model.getLineContent(lineNumber);
          
          // Check if the range overlaps with PROMPT
          // Find PROMPT in the line
          const promptMatch = lineText.match(/\bPROMPT\b/i);
          if (!promptMatch) {
            return { actions: [], dispose: () => {} };
          }
          
          const promptStartCol = promptMatch.index + 1; // Monaco is 1-indexed
          const promptEndCol = promptStartCol + 6; // "PROMPT" is 6 characters
          
          // Check if the selection range overlaps with PROMPT
          const rangeStart = range.startColumn;
          const rangeEnd = range.endColumn;
          const overlapsPrompt = (rangeStart <= promptEndCol && rangeEnd >= promptStartCol);
          
          if (!overlapsPrompt) {
            return { actions: [], dispose: () => {} };
          }
          
          // Parse the PROMPT line using the start of the range or the start of PROMPT
          const cursorColumn = Math.max(rangeStart, promptStartCol);
          const promptInfo = parsePromptLine(lineText, cursorColumn);
          if (!promptInfo) {
            // If parsePromptLine fails, create a basic prompt info
            const afterPrompt = lineText.substring(promptMatch.index + 6).trim();
            const doubleQuoteMatch = afterPrompt.match(/^"([^"]*)"(.*)$/);
            const singleQuoteMatch = !doubleQuoteMatch ? afterPrompt.match(/^'([^']*)'(.*)$/) : null;
            
            const basicPromptInfo = {
              promptStartCol,
              promptEndCol,
              hasQuotes: !!(doubleQuoteMatch || singleQuoteMatch),
              existingText: doubleQuoteMatch ? doubleQuoteMatch[1] : (singleQuoteMatch ? singleQuoteMatch[1] : ''),
              options: doubleQuoteMatch ? doubleQuoteMatch[2].trim() : (singleQuoteMatch ? singleQuoteMatch[2].trim() : ''),
              quoteStartCol: null,
              quoteEndCol: null,
              hasQuotesAroundPrompt: false
            };
            
            // Create code action
            const action = {
              title: 'Open Prompt Generator',
              kind: monaco.languages.CodeActionKind.QuickFix.value,
              isPreferred: true,
              _lineNumber: lineNumber,
              _promptInfo: basicPromptInfo
            };
            
            return {
              actions: [action],
              dispose: () => {}
            };
          }
          
          // Create code action with parsed info
          const action = {
            title: 'Open Prompt Generator',
            kind: monaco.languages.CodeActionKind.QuickFix.value,
            isPreferred: true,
            _lineNumber: lineNumber,
            _promptInfo: promptInfo
          };
          
          return {
            actions: [action],
            dispose: () => {}
          };
        } catch (error) {
          console.error('Error in provideCodeActions:', error);
          return { actions: [], dispose: () => {} };
        }
      },
      resolveCodeAction: (codeAction, token) => {
        // Execute handler when code action is resolved (clicked)
        if (codeAction._lineNumber && codeAction._promptInfo && promptCommandHandlerRef.current) {
          promptCommandHandlerRef.current(codeAction._lineNumber, codeAction._promptInfo);
        }
        return Promise.resolve(codeAction);
      }
    });
    
  }, [parsePromptLine]);

  // Handle prompt drop-in from Prompt Generator Modal
  const handlePromptDropIn = useCallback((promptText) => {
    if (!promptLineInfo || !editorRef.current) return;
    
    const editor = editorRef.current;
    const model = editor.getModel();
    if (!model) return;
    
    const { lineNumber, hasQuotes, existingText, options, quoteStartCol, quoteEndCol } = promptLineInfo;
    const lineText = model.getLineContent(lineNumber);
    
    // Get indentation from the PROMPT line (preserve leading whitespace)
    const indentMatch = lineText.match(/^(\s*)/);
    const indent = indentMatch ? indentMatch[1] : '';
    
    // Find PROMPT position (handling possible quotes around PROMPT itself)
    let promptMatch = lineText.match(/\bPROMPT\b/i);
    if (!promptMatch) {
      const quotedMatch = lineText.match(/["']PROMPT["']/i);
      if (quotedMatch) {
        // Remove quotes around PROMPT and rebuild
        promptMatch = { index: quotedMatch.index };
        
        // Process multi-line prompt: preserve relative indentation within the prompt
        // For continuation lines, add minimal indentation (3 spaces) to align with content
        let processedPromptText = promptText;
        if (promptText.includes('\n')) {
          const lines = promptText.split('\n');
          // Use minimal indentation for continuation lines (3 spaces)
          const continuationIndent = ' '.repeat(3);
          processedPromptText = lines.map((line, idx) => {
            if (idx === 0) return line;
            // Trim the line and add continuation indent
            const trimmedLine = line.trimStart();
            // If line has content, add continuation indent; empty lines stay empty
            return trimmedLine ? continuationIndent + trimmedLine : '';
          }).join('\n');
        }
        
        // Build new line: indent + PROMPT + space + "prompt text" + options
        let newLineText = indent + 'PROMPT "' + processedPromptText + '"';
        if (options) {
          newLineText += ' ' + options;
        }
        
        editor.executeEdits('prompt-drop-in', [{
          range: {
            startLineNumber: lineNumber,
            startColumn: 1,
            endLineNumber: lineNumber,
            endColumn: model.getLineMaxColumn(lineNumber)
          },
          text: newLineText
        }]);
        
        setShowPromptGeneratorModal(false);
        setPromptLineInfo(null);
        modalOpenRef.current = false;
        return;
      } else {
        return;
      }
    }
    
    const promptEndPos = promptMatch.index + 6;
    const afterPrompt = lineText.substring(promptEndPos);
    
    // Process multi-line prompt: preserve relative indentation within the prompt
    let processedPromptText = promptText;
    if (promptText.includes('\n')) {
      const lines = promptText.split('\n');
      // Use minimal indentation for continuation lines (3 spaces)
      const continuationIndent = ' '.repeat(3);
      processedPromptText = lines.map((line, idx) => {
        if (idx === 0) return line;
        // Trim the line and add continuation indent
        const trimmedLine = line.trimStart();
        // If line has content, add continuation indent; empty lines stay empty
        return trimmedLine ? continuationIndent + trimmedLine : '';
      }).join('\n');
    }
    
    // Check if there are existing quotes
    if (hasQuotes && quoteStartCol && quoteEndCol) {
      // Replace text between existing quotes
      const beforeQuotes = lineText.substring(0, quoteStartCol - 1);
      let afterQuotes = lineText.substring(quoteEndCol - 1);
      
      // Clean up afterQuotes - only preserve recognized options, remove stray characters
      const trimmedAfterQuotes = afterQuotes.trim();
      if (trimmedAfterQuotes && !trimmedAfterQuotes.match(/^(OPTIMIZE|FORMAT|MATCH|AGGREGATE|SEPARATE|SCREEN|CACHE|NOCACHE|TEMP|TOP_P|XACT|UNTIL|WHILE|BREAK|SKIP|RETRY|HALT|TRACK)/i)) {
        // Not a recognized option, remove it (including stray characters like "}")
        afterQuotes = '';
      } else if (trimmedAfterQuotes) {
        // It's a recognized option, preserve it with proper spacing
        afterQuotes = ' ' + trimmedAfterQuotes;
      } else {
        afterQuotes = '';
      }
      
      // Always ensure space after PROMPT - check if beforeQuotes ends with PROMPT
      const trimmedBefore = beforeQuotes.trimEnd();
      let spaceAfterPrompt = '';
      if (trimmedBefore.endsWith('PROMPT')) {
        // Check if there's already a space before the quote (character at quoteStartCol - 2)
        const charBeforeQuote = quoteStartCol > 1 ? lineText[quoteStartCol - 2] : '';
        if (charBeforeQuote !== ' ') {
          spaceAfterPrompt = ' ';
        }
      }
      
      const newLineText = beforeQuotes + spaceAfterPrompt + '"' + processedPromptText + '"' + afterQuotes;
      
      editor.executeEdits('prompt-drop-in', [{
        range: {
          startLineNumber: lineNumber,
          startColumn: 1,
          endLineNumber: lineNumber,
          endColumn: model.getLineMaxColumn(lineNumber)
        },
        text: newLineText
      }]);
    } else {
      // No quotes exist - insert PROMPT "text" with 1 space after PROMPT
      // Build the new line preserving indentation - always include space after PROMPT
      let newLineText = indent + 'PROMPT "' + processedPromptText + '"';
      
      // Add options if they exist
      if (options) {
        newLineText += ' ' + options;
      } else {
        // If there's text after PROMPT that's not options, preserve it
        const trimmedAfter = afterPrompt.trim();
        if (trimmedAfter && !trimmedAfter.match(/^(OPTIMIZE|FORMAT|MATCH|AGGREGATE|SEPARATE|SCREEN|CACHE|NOCACHE|TEMP|TOP_P|XACT|UNTIL|WHILE|BREAK|SKIP|RETRY|HALT|TRACK)/i)) {
          newLineText += ' ' + trimmedAfter;
        }
      }
      
      editor.executeEdits('prompt-drop-in', [{
        range: {
          startLineNumber: lineNumber,
          startColumn: 1,
          endLineNumber: lineNumber,
          endColumn: model.getLineMaxColumn(lineNumber)
        },
        text: newLineText
      }]);
    }
    
    // Close modal
    setShowPromptGeneratorModal(false);
    setPromptLineInfo(null);
    modalOpenRef.current = false;
  }, [promptLineInfo]);

  // Deep-link loading moved below to avoid temporal dead zone for applyMarkers

  const setBusy = (key, val) => setPending(p => ({ ...p, [key]: val }));

  // Gate: Pro/Admin/Team/Enterprise users only
  useEffect(() => {
    (async () => {
      try {
        const info = await authService.getUserInfo();
        const roles = (info?.roles || []).map(r => String(r).toLowerCase());
        const isPaid = roles.includes('paiduser') || roles.includes('pro');
        const isAdmin = roles.includes('admin');
        const isTeam = roles.includes('teamuser') || roles.includes('teamadmin');
        const isEnt = roles.includes('enterpriseuser') || roles.includes('enterpriseadmin');
        setIsPro(isPaid || isAdmin || isTeam || isEnt);
      } catch { setIsPro(false); }
    })();
  }, []);

  // Persist UI mode
  useEffect(() => {
    try { localStorage.setItem('baiql_mode', uiMode); } catch {}
  }, [uiMode]);

  // Persist Mini‑BAIQL state immediately on change
  useEffect(() => {
    try { localStorage.setItem('mini_baiql_state_v1', JSON.stringify(miniState)); } catch {}
  }, [miniState]);

  // Keyboard shortcuts: F6 or Ctrl+Shift+M toggle Basic/Pro
  useEffect(() => {
    const handler = (e) => {
      // Inactive when any modal is open
      if (document.querySelector('.app-modal')) return;
      const isF6 = e.key === 'F6';
      const isCtrlShiftM = e.ctrlKey && e.shiftKey && (e.key === 'M' || e.key === 'm');
      if (!isF6 && !isCtrlShiftM) return;
      e.preventDefault();
      setUiMode(m => (m === 'Basic' ? 'Pro' : 'Basic'));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // handleSave implemented below near menuBar to avoid duplicate definitions

  const applyMarkers = useCallback((valResult) => {
    const monaco = monacoRef.current;
    const editor = editorRef.current;
    if (!monaco || !editor) return;
    const model = editor.getModel();
    if (!model) return;
    const markers = (valResult.errors || []).map(e => ({
      startLineNumber: e.line || 1,
      endLineNumber: e.line || 1,
      startColumn: 1,
      endColumn: Math.max(2, (model.getLineLength(e.line || 1) || 1) + 1),
      message: e.message,
      severity: e.severity === 'warning' ? monaco.MarkerSeverity.Warning : monaco.MarkerSeverity.Error
    }));
    monaco.editor.setModelMarkers(model, 'baiql', markers);
  }, []);

  // Helper: load a script by id (used by Load modal and deep-link open)
  const loadScriptById = useCallback(async (idToLoad) => {
    const sid = parseInt(idToLoad, 10);
    if (!sid || Number.isNaN(sid)) throw new Error('Invalid script id');
    const s = await getScript(sid);
    const metaResp = await getScriptMeta(sid).catch(() => null);
    setScriptId(s.id);
    setScriptName(s.name || 'Untitled');
    setCode(s.content || '');
    setBatchType(null);
    try { applyMarkers({ errors: [] }); } catch {}
    setErrors([]);
    setOutput('');
    setStatus(`Loaded "${s.name}"`);
    setLastSaved(new Date(s.modified || Date.now()));
    setDirty(false);
    if (metaResp) {
      setMeta({
        version: metaResp.version || '',
        author: metaResp.author || '',
        description: metaResp.description || '',
        changelog: metaResp.changelog || '',
        sharedWith: metaResp.sharedWith || '',
        approvalRequiredFrom: metaResp.approvalRequiredFrom || '',
        inheritFrom: metaResp.inheritFrom || '',
        emitMetaAsTags: !!metaResp.emitMetaAsTags,
        lockEditsWithoutApproval: !!metaResp.lockEditsWithoutApproval
      });
    }
    try { localStorage.setItem('baiql_last', s.content || ''); } catch {}
  }, [applyMarkers]);

  // Deep-link helpers declared below

  // Handle deep-link open from Scripts Library via state or ?scriptId=
  useEffect(() => {
    try {
      const st = (location && location.state) || {};
      const fromState = st && (st.scriptId || st.id);
      const params = new URLSearchParams(location?.search || '');
      const fromQuery = params.get('scriptId');
      const requested = fromState || fromQuery;
      if (!requested) return;
      if (String(requested) === String(scriptId || '')) return;
      (async () => {
        if (dirty) {
          const saveFirst = window.confirm('You have unsaved changes. Save before opening the selected script?');
          if (saveFirst) {
            await handleSave();
          }
        }
        try {
          await loadScriptById(requested);
        } catch (e) {
          setToast({ show:true, type:'error', message:(e && e.message) ? e.message : 'Failed to open script' });
        }
      })();
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, /* do not include handleSave to avoid prompt loops */ dirty, scriptId]);

  const runValidation = useCallback(() => {
    try {
      const res = validateBaiql(code || '');
      setErrors(res.errors || []);
      setBatchType(res.batchType || null);
      applyMarkers(res);
      const errCount = (res.errors || []).filter(e => e.severity !== 'warning').length;
      const warnCount = (res.errors || []).filter(e => e.severity === 'warning').length;
      setStatus(errCount === 0 ? (warnCount === 0 ? '✓ Valid' : `✓ Valid with ${warnCount} warning${warnCount!==1?'s':''}`) : `✗ ${errCount} error${errCount!==1?'s':''} found`);
      setOutput(errCount === 0 && warnCount === 0 ? 'Validation successful' : (res.errors || []).map(e => `${e.severity.toUpperCase()} [${e.line}]: ${e.message}`).join('\n'));
    } catch (e) {
      setStatus('✗ Validation failed');
    }
  }, [code, applyMarkers]);

  const debouncedValidate = useMemo(() => debounceFn(runValidation, 500), [runValidation]);

  useEffect(() => {
    debouncedValidate();
    setDirty(true);
    // Persist code to localStorage for local state only (not saving to library)
    try { localStorage.setItem('baiql_last', code || ''); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleValidate = useCallback(async () => {
    setBusy('validate', true); setStatus('');
    try {
      // client-side first
      runValidation();
      // backend validate
      const t = authService.getToken && authService.getToken ? authService.getToken() : null;
      const r = await fetch('/api/baiql/execute', { method:'POST', headers: { 'Content-Type':'application/json', ...(t ? { Authorization:`Bearer ${t}` } : {}) }, credentials:'include', body: JSON.stringify({ scriptContent: code || '', mode: 'VALIDATE', userId: null }) });
      if (r.ok) {
        const j = await r.json();
        setBatchType(j.batchType || batchType);
        if (Array.isArray(j.validationErrors) && j.validationErrors.length) {
          const mapped = j.validationErrors.map(e => ({ line: e.line || 1, message: e.message || 'Validation error', severity: 'error' }));
          setErrors(mapped);
          applyMarkers({ errors: mapped });
          setStatus(`✗ ${mapped.length} error${mapped.length!==1?'s':''} found`);
        }
      }
    } catch {
      // ignore backend errors in validate path
    } finally { setBusy('validate', false); }
  }, [runValidation, code, applyMarkers, batchType]);

  const extractPathInFromCode = useCallback((src) => {
    try { const m = (src||'').match(/^(?:\s*GLOBAL\s+)?PATH_IN\s+"([^"]+)"/im); return m ? m[1] : ''; } catch { return ''; }
  }, []);

  const detectFileLoadInCode = useCallback((src) => {
    try {
      const re = /\bLOAD\b[^\n\r]*\bAS\s+(FILE|WORK)\b/i;
      return re.test(src || '');
    } catch {
      return false;
    }
  }, []);

  // Check if agent is running
  const checkAgentStatus = useCallback(async () => {
    try {
      // Use a timeout to detect if agent is running
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      try {
        const response = await fetch('http://localhost:3737/health', {
          method: 'GET',
          signal: controller.signal,
          cache: 'no-cache'
        });
        clearTimeout(timeoutId);
        if (response.ok) {
          setAgentRunning(true);
          
          // Check if agent is paired
          const token = authService.getToken && authService.getToken ? authService.getToken() : null;
          if (token) {
            try {
              const statusRes = await fetch('/api/pairing/status', {
                method: 'GET',
                headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
                credentials: 'include'
              });
              if (statusRes.ok) {
                const statusData = await statusRes.json();
                setAgentPaired(statusData.devices && statusData.devices.length > 0);
              } else {
                setAgentPaired(false);
              }
            } catch {
              setAgentPaired(false);
            }
          } else {
            setAgentPaired(false);
          }
        } else {
          setAgentRunning(false);
          setAgentPaired(false);
        }
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          // Timeout - agent not running
          setAgentRunning(false);
          setAgentPaired(false);
        } else {
          // Network error - likely CORS or agent not running
          setAgentRunning(false);
          setAgentPaired(false);
        }
      }
    } catch {
      setAgentRunning(false);
      setAgentPaired(false);
    }
  }, []);

  // Generate pairing code
  const generatePairingCode = useCallback(async () => {
    try {
      const token = authService.getToken && authService.getToken ? authService.getToken() : null;
      if (!token) {
        setToast({ show: true, type: 'error', message: 'Please log in to pair agent' });
        return;
      }

      const response = await fetch('/api/pairing/generate', {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPairingCode(data.code);
        setShowPairingModal(true);
      } else {
        const error = await response.json();
        setToast({ show: true, type: 'error', message: error.error || 'Failed to generate pairing code' });
      }
    } catch (error) {
      setToast({ show: true, type: 'error', message: 'Failed to generate pairing code' });
    }
  }, []);

  // Check agent status on mount and when needed
  useEffect(() => {
    checkAgentStatus();
    // Check periodically
    const interval = setInterval(checkAgentStatus, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [checkAgentStatus]);

  const handleExecute = useCallback(async () => {
    // Check tier-based execution permission
    if (!canAccessFeature('BAIQL_EXECUTE')) {
      const message = getFeatureDenialMessage('BAIQL_EXECUTE');
      setToast({
        show: true,
        type: 'warning',
        message: message || 'Add $10 to unlock BAIQL execution'
      });
      setStatus('✗ ' + (message || 'Execution not available'));
      return;
    }
    
    const errCount = (errors || []).filter(e => e.severity !== 'warning').length;
    if (errCount > 0) { setStatus('✗ Fix validation errors first'); return; }
    
    // Check if agent is required (when PATH_IN is used with file operations)
    let payloadCode = code || '';
    const pathIn = extractPathInFromCode(payloadCode);
    const needsUpload = !!pathIn && detectFileLoadInCode(payloadCode);
    
    if (needsUpload) {
      // Check agent status
      await checkAgentStatus();
      
      // Wait a moment for state to update
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Re-check state after async update
      let isRunning = false;
      let isPaired = false;
      try {
        const response = await fetch('http://localhost:3737/health', {
          method: 'GET',
          signal: AbortSignal.timeout(2000)
        });
        isRunning = response.ok;
        
        if (isRunning) {
          const token = authService.getToken && authService.getToken ? authService.getToken() : null;
          if (token) {
            try {
              const statusRes = await fetch('/api/pairing/status', {
                method: 'GET',
                headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
                credentials: 'include'
              });
              if (statusRes.ok) {
                const statusData = await statusRes.json();
                isPaired = statusData.devices && statusData.devices.length > 0;
              }
            } catch {}
          }
        }
      } catch {}
      
      if (!isRunning) {
        setToast({
          show: true,
          type: 'warning',
          message: "PocketPE Agent is required for file transfers. Click the 'Download Agent' button above."
        });
        setStatus('✗ Agent required for file operations');
        return;
      }
      
      if (isRunning && !isPaired) {
        // Show pairing prompt
        let shouldPair = false;
        setToast({
          show: true,
          type: 'warning',
          message: "PocketPE Agent is running but needs to be paired. Pair now?",
          persist: true,
          actionLabel: 'Pair',
          onAction: async () => {
            shouldPair = true;
            setToast({ show: false });
            // Generate pairing code and show modal
            await generatePairingCode();
            setStatus('✗ Please complete pairing to continue');
          },
          secondaryActionLabel: 'Cancel',
          onSecondaryAction: () => {
            shouldPair = false;
            setToast({ show: false });
            setStatus('✗ Execution cancelled - agent not paired');
          }
        });
        
        // Wait for user decision (they'll click Pair or Cancel)
        return;
      }
    }
    
    const bt = batchType || 'Unknown';
    let cancelled = false;
    const clearToast = () => setToast(prev => ({ ...prev, show: false }));
    const showExecutingToast = () => setToast({
      show: true,
      type: 'info',
      message: `Executing BAIQL (${bt})…`,
      persist: true,
      actionLabel: 'Cancel',
      onAction: () => {
        clearToast();
        setToast({
          show: true,
          type: 'warning',
          message: 'Confirm cancel execution?',
          persist: true,
          actionLabel: 'Confirm',
          onAction: () => {
            cancelled = true;
            clearToast();
            setStatus('✗ Execution cancelled');
            setOutput(prev => `${prev ? prev + '\n' : ''}Execution halted by User`);
            try { console.warn('BAIQL: Execution halted by User'); } catch {}
            setToast({ show:true, type:'error', message:'Execution halted by User', duration:1000 });
          },
          secondaryActionLabel: 'Resume',
          onSecondaryAction: () => { clearToast(); showExecutingToast(); }
        });
      }
    });

    showExecutingToast();
    setBusy('execute', true); setStatus('Executing…');

    if (needsUpload && uploadsEnabled) {
      try {
        const chooseFiles = () => new Promise((resolve) => {
          if (!inputUploadInRef.current) {
            resolve([]);
            return;
          }
          const input = inputUploadInRef.current;
          input.value = '';
          const handler = (e) => {
            try {
              const files = Array.from(e.target.files || []);
              resolve(files);
            } finally {
              input.removeEventListener('change', handler);
            }
          };
          input.addEventListener('change', handler);
          input.click();
        });

        const files = await chooseFiles();
        if (!files || files.length === 0) {
          clearToast();
          setBusy('execute', false);
          setStatus('✗ No files selected for PATH_IN');
          setToast({ show:true, type:'warning', message:'No files selected for PATH_IN' });
          return;
        }

        const fd = new FormData();
        fd.append('pathIn', pathIn);
        files.forEach(f => fd.append('files', f, f.name));

        const tkn = authService.getToken && authService.getToken ? authService.getToken() : null;
        const uploadRes = await fetch('/api/baiql/uploads', {
          method: 'POST',
          headers: { ...(tkn ? { Authorization:`Bearer ${tkn}` } : {}) },
          body: fd,
          credentials: 'include'
        });
        if (!uploadRes.ok) {
          throw new Error(await uploadRes.text());
        }
        const uploadJson = await uploadRes.json().catch(() => ({}));
        if (!uploadJson || uploadJson.success !== true) {
          throw new Error(uploadJson?.message || 'Upload failed');
        }
      } catch (uploadErr) {
        clearToast();
        setBusy('execute', false);
        const msg = (uploadErr && uploadErr.message) ? uploadErr.message : 'Upload failed';
        setStatus('✗ ' + msg);
        setToast({ show:true, type:'error', message: msg });
        return;
      }
    }

    await new Promise(res => setTimeout(res, 1000));
    if (cancelled) { setBusy('execute', false); return; }

    clearToast();
    setToast({ show:true, type:'success', message:'Queued execution… (cannot cancel once started)', duration:500 });
    await new Promise(res => setTimeout(res, 500));
    if (cancelled) { setBusy('execute', false); return; }

    try {
      const t = authService.getToken && authService.getToken ? authService.getToken() : null;
      if (meta?.emitMetaAsTags) {
        const lines = [];
        const pushTag = (k, v) => { if (v && String(v).trim()) lines.push(`TAG ${k} = "${String(v).replace(/"/g, '\\"')}"`); };
        pushTag('version', meta.version);
        pushTag('author', meta.author);
        pushTag('description', meta.description);
        pushTag('changelog', meta.changelog);
        pushTag('shared_with', meta.sharedWith);
        pushTag('approval_required_from', meta.approvalRequiredFrom);
        pushTag('inherit_from', meta.inheritFrom);
        if (lines.length) payloadCode = `${lines.join('\n')}\n${payloadCode}`;
      }
      const r = await fetch('/api/baiql/execute', { method:'POST', headers: { 'Content-Type':'application/json', ...(t ? { Authorization:`Bearer ${t}` } : {}) }, credentials:'include', body: JSON.stringify({ scriptContent: payloadCode, mode: 'EXECUTE', userId: null }) });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      const execId = j?.executionId;
      const containsScreen = /\bOUTPUT\b[\s\S]*?\bSCREEN\b/i.test(payloadCode || '');
      if (execId && navigate) {
        navigate(`/baiql/exec/${execId}`);
      } else {
        setStatus('Queued (cannot cancel once started)');
      }
    } catch (e) {
      setStatus('✗ Execute failed');
      const errorMessage = (e && e.message) ? String(e.message) : 'Execute failed';
      const errorTextLower = errorMessage.toLowerCase();
      const insufficientKeywords = ['insufficient', 'balance', 'credit', 'funds', 'out of money', 'no money', 'zero balance', 'low balance', 'not enough'];
      const budgetKeywords = ['budget', 'limit exceeded', 'daily budget', 'monthly budget', 'budget limit', 'exceeded'];
      let hasInsufficientError = insufficientKeywords.some(keyword => errorTextLower.includes(keyword));
      let hasBudgetError = budgetKeywords.some(keyword => errorTextLower.includes(keyword));
      
      // If error doesn't contain keywords, check actual balance
      if (!hasInsufficientError) {
        try {
          const t = authService.getToken && authService.getToken ? authService.getToken() : null;
          if (t) {
            const balRes = await fetch('/api/billing/balance', { headers: { Authorization: `Bearer ${t}` }, credentials:'include', cache:'no-store' });
            if (balRes.ok) {
              const balData = await balRes.json();
              const balanceCents = typeof balData?.balanceCents === 'number' ? balData.balanceCents : null;
              if (balanceCents !== null && balanceCents <= 0) {
                hasInsufficientError = true;
                console.log('BAIQL execution failed with zero balance. Error:', errorMessage);
              }
            }
          }
        } catch (balErr) {
          console.warn('Failed to check balance:', balErr);
        }
      }
      
      try { 
        if (hasBudgetError) {
          setToast({ 
            show:true, 
            type:'error', 
            message:errorMessage || 'Execution failed due to budget limit exceeded. Please contact your administrator to adjust budget limits.',
            duration: 10000
          }); 
        } else if (hasInsufficientError) {
          setToast({ 
            show:true, 
            type:'error', 
            message:'Execution failed due to insufficient balance. Please add funds to your account to continue.',
            duration: 8000
          }); 
        } else {
          setToast({ show:true, type:'error', message:errorMessage }); 
        }
      } catch {}
    } finally { setBusy('execute', false); }
  }, [errors, batchType, code, navigate, canAccessFeature, getFeatureDenialMessage, meta, extractPathInFromCode, detectFileLoadInCode]);

  const clearMarkers = useCallback(() => {
    try { applyMarkers({ errors: [] }); } catch {}
    setErrors([]);
  }, [applyMarkers]);

  const handleClear = useCallback(() => {
    // Keep existing script name and id; just clear the editor to a blank starting line
    setCode('');
    setBatchType(null);
    clearMarkers();
    setOutput('');
    setStatus('');
    setDirty(true);
    try {
      localStorage.removeItem('baiql_last');
      localStorage.removeItem('mini_baiql_state_v1');
    } catch {}
    try { setMiniState({ ...miniDefaults }); } catch {}
  }, [clearMarkers]);

  const generateDefaultScriptName = useCallback(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `Untitled-${y}${m}${d}-${hh}${mm}${ss}`;
  }, []);

  const performNew = useCallback(() => {
    const def = generateDefaultScriptName();
    setScriptId(null);
    setScriptName(def);
    setCode('');
    setBatchType(null);
    clearMarkers();
    setOutput('');
    setStatus('');
    setLastSaved(null);
    setDirty(false);
    try {
      localStorage.removeItem('baiql_last');
      localStorage.removeItem('mini_baiql_state_v1');
    } catch {}
    try { setMiniState({ ...miniDefaults }); } catch {}
  }, [generateDefaultScriptName, clearMarkers]);

  const handleNew = useCallback(() => {
    if (dirty) { setShowConfirmNew(true); return; }
    performNew();
  }, [dirty, performNew]);

  // SCREEN support: polling and actions
  const startScreenPolling = useCallback((execId) => {
    try { if (screenPollerRef.current) { clearInterval(screenPollerRef.current); screenPollerRef.current = null; } } catch {}
    screenPollerRef.current = setInterval(async () => {
      try {
        const tkn = authService.getToken && authService.getToken ? authService.getToken() : null;
        const resp = await fetch(`/api/baiql/execute/${execId}/stream`, { headers: tkn ? { Authorization:`Bearer ${tkn}` } : {}, credentials:'include' });
        if (!resp.ok) return;
        const js = await resp.json();
        const chunks = Array.isArray(js?.chunks) ? js.chunks : [];
        for (let i = chunks.length - 1; i >= 0; i--) {
          const c = chunks[i];
          try {
            const obj = JSON.parse(c);
            if (obj && obj.type === 'screen') {
              setScreenState(prev => ({ ...prev, execId, file: String(obj.file||'results.txt'), format: String(obj.format||'txt'), content: String(obj.content||''), ready:true }));
              return;
            }
          } catch {}
        }
      } catch {}
    }, 1200);
  }, []);

  const extractPathOutFromCode = useCallback((src) => {
    try { const m = (src||'').match(/^(?:\s*GLOBAL\s+)?PATH_OUT\s+"([^"]+)"/im); return m ? m[1] : ''; } catch { return ''; }
  }, []);

  const handleScreenSave = useCallback(async () => {
    const execId = screenState.execId; if (!execId) return;
    const hasPathOut = !!extractPathOutFromCode(code||'');
    if (!hasPathOut) { setToast({ show:true, type:'error', message:'Set PATH_OUT before saving.' }); return; }
    try {
      setScreenState(prev => ({ ...prev, saving:true }));
      const t = authService.getToken && authService.getToken ? authService.getToken() : null;
      const body = { executionId: execId, fileName: screenState.file, format: screenState.format, content: screenState.content };
      const r = await fetch('/api/baiql/screen/save', { method:'POST', headers: { 'Content-Type':'application/json', ...(t ? { Authorization:`Bearer ${t}` } : {}) }, credentials:'include', body: JSON.stringify(body) });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      setToast({ show:true, type:'success', message:`Saved to ${j?.path || 'file'}` });
      setScreenState({ show:false, execId:null, file:'', format:'txt', content:'', ready:false, saving:false });
      try { if (screenPollerRef.current) { clearInterval(screenPollerRef.current); screenPollerRef.current = null; } } catch {}
      // Resume execution
      try { const t2 = authService.getToken && authService.getToken ? authService.getToken() : null; await fetch(`/api/baiql/execute/${execId}/resume`, { method:'POST', headers: t2 ? { Authorization:`Bearer ${t2}` } : {}, credentials:'include' }); } catch {}
    } catch (e) {
      setToast({ show:true, type:'error', message:(e && e.message) ? e.message : 'Save failed' });
      setScreenState(prev => ({ ...prev, saving:false }));
    }
  }, [screenState, code]);

  const handleScreenDiscard = useCallback(async () => {
    const execId = screenState.execId; if (!execId) return;
    try {
      setScreenState(prev => ({ ...prev, saving:true }));
      const t = authService.getToken && authService.getToken ? authService.getToken() : null;
      const body = { executionId: execId, fileName: screenState.file, format: screenState.format, content: screenState.content };
      await fetch('/api/baiql/screen/discard', { method:'POST', headers: { 'Content-Type':'application/json', ...(t ? { Authorization:`Bearer ${t}` } : {}) }, credentials:'include', body: JSON.stringify(body) });
      setToast({ show:true, type:'success', message:'Discarded to audit.' });
      setScreenState({ show:false, execId:null, file:'', format:'txt', content:'', ready:false, saving:false });
      try { if (screenPollerRef.current) { clearInterval(screenPollerRef.current); screenPollerRef.current = null; } } catch {}
      try { const t2 = authService.getToken && authService.getToken ? authService.getToken() : null; await fetch(`/api/baiql/execute/${execId}/resume`, { method:'POST', headers: t2 ? { Authorization:`Bearer ${t2}` } : {}, credentials:'include' }); } catch {}
    } catch (e) {
      setToast({ show:true, type:'error', message:(e && e.message) ? e.message : 'Discard failed' });
      setScreenState(prev => ({ ...prev, saving:false }));
    }
  }, [screenState]);

  const handleLoad = useCallback(async () => {
    try {
      setLoadingList(true); setShowLoad(true);
      let list = await listScripts(null).catch(() => []);
      if (!Array.isArray(list) || list.length === 0) {
        try {
          const t = authService.getToken && authService.getToken ? authService.getToken() : null;
          const r = await fetch('/api/baiql/execute/scripts', { headers: { ...(t ? { Authorization:`Bearer ${t}` } : {}) }, credentials:'include' });
          if (r.ok) {
            const alt = await r.json();
            if (Array.isArray(alt)) list = alt.map(x => ({ id: x.id || x.scriptId || x.Id, name: x.name || x.Name, modified: x.modified || x.modifiedAt || x.Modified || x.ModifiedAt }));
          }
        } catch { }
      }
      setScriptList(Array.isArray(list) ? list : []);
      if (!Array.isArray(list) || list.length === 0) setToast({ show:true, type:'warning', message:'No scripts found' });
    } catch {
      setScriptList([]);
      setToast({ show:true, type:'error', message:'Failed to load script list' });
    } finally { setLoadingList(false); }
  }, []);

  const handleOpenLocal = useCallback(() => {
    try { if (fileOpenRef.current) fileOpenRef.current.click(); } catch {}
  }, []);

  const handleLocalFileSelected = useCallback((e) => {
    try {
      const f = e?.target?.files && e.target.files[0];
      if (!f) return;
      const reader = new FileReader();
      reader.onload = () => {
        const text = String(reader.result || '');
        setCode(text);
        setBatchType(null);
        clearMarkers();
        setOutput('');
        setStatus('Loaded from file');
        setDirty(true);
        setScriptId(null);
        const base = (f.name || 'Untitled').replace(/\.[^.]+$/, '');
        setScriptName(base);
        try { localStorage.setItem('baiql_last', text); } catch {}
      };
      reader.readAsText(f);
    } catch {}
  }, [clearMarkers]);

  const handleSaveAs = useCallback(async () => {
    const suggested = scriptName && scriptName !== 'Untitled' ? scriptName : generateDefaultScriptName();
    setSaveAsName(suggested);
    setShowSaveAsModal(true);
  }, [scriptName, generateDefaultScriptName]);

  const handleExport = useCallback(() => {
    try {
      const text = String(code || '');
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const base = (scriptName || 'Untitled').replace(/[^A-Za-z0-9-_\. ]/g, '').trim() || 'Untitled';
      a.href = url;
      a.download = base.endsWith('.bql') ? base : `${base}.bql`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
    } catch {}
  }, [code, scriptName]);

  const triggerEditor = useCallback((actionId) => {
    try {
      const editor = editorRef.current; const monaco = monacoRef.current;
      if (!editor || !monaco) return;
      editor.focus();
      editor.trigger('keyboard', actionId);
    } catch {}
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const id = scriptId;
      const name = (scriptName || '').trim();
      const text = code || '';
      if (id) {
        await updateScript(id, name || 'Untitled', text);
        setLastSaved(new Date());
        setDirty(false);
        setStatus('✓ Saved');
        try { setToast({ show:true, type:'success', message:'Saved', duration:1200 }); } catch {}
      } else {
        // New/untitled script → open Save As
        handleSaveAs();
      }
    } catch (e) {
      try { setToast({ show:true, type:'error', message: (e && e.message) ? e.message : 'Save failed' }); } catch {}
    }
  }, [scriptId, scriptName, code, updateScript, setLastSaved, setDirty, setStatus, setToast, handleSaveAs]);

  const menuBar = useMemo(() => (
    <div style={{ display:'flex', gap:24, alignItems:'center', borderBottom:'1px solid #1f2937', padding:'6px 8px', background:'#0b1020' }}>
      {/* File */}
      <div style={{ position:'relative' }}>
        <button className="docs-tab" onClick={() => setOpenMenu(m => m==='file'?null:'file')}>File</button>
        {openMenu === 'file' && (
          <div style={{ position:'absolute', top:'100%', left:0, background:'#0b1020', border:'1px solid #1f2937', minWidth:260, zIndex:5 }} onMouseLeave={()=>setOpenMenu(null)}>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); handleNew(); }}>
              <span>New</span><span style={{ color:'#9ca3af' }}>Ctrl+N</span>
            </button>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); handleOpenLocal(); }}>
              <span>Open (from computer)</span><span style={{ color:'#9ca3af' }}>Ctrl+O</span>
            </button>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); handleSave(); }}>
              <span>Save</span><span style={{ color:'#9ca3af' }}>Ctrl+S</span>
            </button>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); handleSaveAs(); }}>
              <span>Save As…</span><span style={{ color:'#9ca3af' }}>Ctrl+Shift+S</span>
            </button>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); handleExport(); }}>
              <span>Export (.bql)</span><span style={{ color:'#9ca3af' }}>Ctrl+E</span>
            </button>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); setShowTemplates(true); }}>
              <span>Templates</span><span style={{ color:'#9ca3af' }}>&nbsp;</span>
            </button>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); handleClear(); }}>
              <span>Clear</span><span style={{ color:'#9ca3af' }}>&nbsp;</span>
            </button>
          </div>
        )}
        <input ref={fileOpenRef} type="file" accept=".bql,.txt,.md,.json,.sql,.csv" style={{ display:'none' }} onChange={handleLocalFileSelected} />
      </div>

      {/* Library */}
      <div style={{ position:'relative' }}>
        <button className="docs-tab" onClick={() => setOpenMenu(m => m==='library'?null:'library')}>Library</button>
        {openMenu === 'library' && (
          <div style={{ position:'absolute', top:'100%', left:0, background:'#0b1020', border:'1px solid #1f2937', minWidth:220, zIndex:5 }} onMouseLeave={()=>setOpenMenu(null)}>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); handleSave(); }}>
              <span>Save Script</span><span style={{ color:'#9ca3af' }}>Ctrl+S</span>
            </button>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); handleLoad(); }}>
              <span>Load Script</span><span style={{ color:'#9ca3af' }}>Ctrl+Shift+O</span>
            </button>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{
              setOpenMenu(null);
              if (scriptId) {
                navigate('/scripts-library', { state: { shareTargetId: scriptId } });
              } else {
                navigate('/scripts-library');
              }
            }}>
              <span>Share Script</span><span style={{ color:'#9ca3af' }}>&nbsp;</span>
            </button>
          </div>
        )}
      </div>

      {/* Edit */}
      <div style={{ position:'relative' }}>
        <button className="docs-tab" onClick={() => setOpenMenu(m => m==='edit'?null:'edit')}>Edit</button>
        {openMenu === 'edit' && (
          <div style={{ position:'absolute', top:'100%', left:0, background:'#0b1020', border:'1px solid #1f2937', minWidth:260, zIndex:5 }} onMouseLeave={()=>setOpenMenu(null)}>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); triggerEditor('undo'); }}>
              <span>Undo</span><span style={{ color:'#9ca3af' }}>Ctrl+Z</span>
            </button>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); triggerEditor('redo'); }}>
              <span>Redo</span><span style={{ color:'#9ca3af' }}>Ctrl+Y</span>
            </button>
            <div style={{ borderTop:'1px solid #1f2937', margin:'4px 0' }} />
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); triggerEditor('editor.action.clipboardCutAction'); }}>
              <span>Cut</span><span style={{ color:'#9ca3af' }}>Ctrl+X</span>
            </button>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); triggerEditor('editor.action.clipboardCopyAction'); }}>
              <span>Copy</span><span style={{ color:'#9ca3af' }}>Ctrl+C</span>
            </button>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); triggerEditor('editor.action.clipboardPasteAction'); }}>
              <span>Paste</span><span style={{ color:'#9ca3af' }}>Ctrl+V</span>
            </button>
            <div style={{ borderTop:'1px solid #1f2937', margin:'4px 0' }} />
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); triggerEditor('editor.action.commentLine'); }}>
              <span>Toggle Line Comment</span><span style={{ color:'#9ca3af' }}>Ctrl+/</span>
            </button>
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); triggerEditor('editor.action.blockComment'); }}>
              <span>Toggle Block Comment</span><span style={{ color:'#9ca3af' }}>Shift+Alt+A</span>
            </button>
            <div style={{ borderTop:'1px solid #1f2937', margin:'4px 0' }} />
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={()=>{ setOpenMenu(null); setShowShortcuts(true); }}>
              <span>Keyboard Shortcuts…</span><span style={{ color:'#9ca3af' }}>F1</span>
            </button>
            <div style={{ borderTop:'1px solid #1f2937', margin:'4px 0' }} />
            <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={async()=>{
              setOpenMenu(null);
              if (scriptId) {
                try {
                  const m = await getScriptMeta(scriptId);
                  setMeta({
                    version: m?.version || '',
                    author: m?.author || '',
                    description: m?.description || '',
                    changelog: m?.changelog || '',
                    sharedWith: m?.sharedWith || '',
                    approvalRequiredFrom: m?.approvalRequiredFrom || '',
                    inheritFrom: m?.inheritFrom || '',
                    emitMetaAsTags: !!m?.emitMetaAsTags,
                    lockEditsWithoutApproval: !!m?.lockEditsWithoutApproval
                  });
                } catch {}
              }
              setShowMeta(true);
            }}>
              <span>Meta…</span><span style={{ color:'#9ca3af' }}>&nbsp;</span>
            </button>
          </div>
        )}
      </div>

      {/* Mode */}
      <div style={{ position:'relative' }}>
        <button className="docs-tab" title="Toggle Basic/Pro (F6 or Ctrl+Shift+M)" onClick={() => setOpenMenu(m => m==='mode'?null:'mode')}>Mode</button>
        {openMenu === 'mode' && (
          <div style={{ position:'absolute', top:'100%', left:0, background:'#0b1020', border:'1px solid #1f2937', minWidth:140, zIndex:5 }} onMouseLeave={()=>setOpenMenu(null)}>
            <button className="docs-tab" style={{ display:'block', width:'100%', textAlign:'left' }} onClick={()=>{ setUiMode('Basic'); setOpenMenu(null); }}>Basic</button>
            <button className="docs-tab" style={{ display:'block', width:'100%', textAlign:'left' }} onClick={()=>{ setUiMode('Pro'); setOpenMenu(null); }}>Pro</button>
          </div>
        )}
      </div>

      {/* Storage (uploads / folders) */}
      {uploadsEnabled && (
        <div style={{ position:'relative' }}>
          <button className="docs-tab" onClick={() => setOpenMenu(m => m==='storage'?null:'storage')}>Storage</button>
          {openMenu === 'storage' && (
            <div style={{ position:'absolute', top:'100%', left:0, background:'#0b1020', border:'1px solid #1f2937', minWidth:260, zIndex:5 }} onMouseLeave={()=>setOpenMenu(null)}>
              <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={async()=>{
                setOpenMenu(null);
                try {
                  if (inputUploadInRef.current) { inputUploadInRef.current.value = null; inputUploadInRef.current.click(); }
                } catch {}
              }}>
                <span>Upload Input…</span><span style={{ color:'#9ca3af' }}>Ctrl+U</span>
              </button>
              <button className="docs-tab" style={{ display:'flex', width:'100%', justifyContent:'space-between' }} onClick={async()=>{
                setOpenMenu(null);
                try {
                  const res = await apiCreateFolder({});
                  const outPath = (res && res.serverPath) ? String(res.serverPath) : '';
                  if (outPath) {
                    setCode(prev => {
                      const lines = String(prev||'').split(/\r?\n/);
                      const pathOutLine = `PATH_OUT "${outPath.replaceAll('/', '\\')}"`;
                      const globalPrefix = '';
                      // remove existing PATH_OUT lines
                      const out = lines.filter(l => !/^\s*(GLOBAL\s+)?PATH_OUT\b/i.test(l||''));
                      // find PATH_IN to place under
                      let pathInIdx = out.findIndex(l => /^\s*(?:GLOBAL\s+)?PATH_IN\b/i.test(l||''));
                      let insertAt = 0;
                      if (pathInIdx >= 0) insertAt = pathInIdx + 1; else {
                        const batchIdx = out.findIndex(l => /^\s*BATCH_START\b/i.test(l||''));
                        insertAt = (batchIdx >= 0) ? batchIdx : 0;
                      }
                      out.splice(insertAt, 0, `${globalPrefix}${pathOutLine}`);
                      // ensure exactly one blank line after inserted line
                      const normIdx = insertAt;
                      if (out[normIdx+1] && out[normIdx+1].trim() !== '') { out.splice(normIdx+1, 0, ''); }
                      // ensure blank line before if not at top
                      if (normIdx-1 >=0 && out[normIdx-1].trim() !== '') { out.splice(normIdx, 0, ''); }
                      return out.join('\n');
                    });
                    setToast({ show:true, type:'success', message:'Created output folder and updated PATH_OUT' });
                  } else {
                    setToast({ show:true, type:'warning', message:'Folder created but no path returned' });
                  }
                } catch (e) {
                  setToast({ show:true, type:'error', message:'Create output folder failed' });
                }
              }}>
                <span>Create Output Folder…</span><span style={{ color:'#9ca3af' }}>Ctrl+Shift+U</span>
              </button>
            </div>
          )}
          <input ref={inputUploadInRef} type="file" style={{ display:'none' }} multiple webkitdirectory="" onChange={async (e)=>{
            try {
              const files = Array.from(e?.target?.files || []);
              if (!files.length) return;
              let jobId;
              try { const job = await apiCreateJob(); jobId = job?.jobId; } catch {}
              const res = await apiUploadFiles({ jobId, files });
              const inPath = (res && res.serverPath) ? String(res.serverPath) : '';
              if (inPath) {
                setCode(prev => {
                  const lines = String(prev||'').split(/\r?\n/);
                  const pathInLine = `PATH_IN "${inPath.replaceAll('/', '\\')}"`;
                  // remove existing PATH_IN lines
                  const out = lines.filter(l => !/^\s*(GLOBAL\s+)?PATH_IN\b/i.test(l||''));
                  // place near top or after BATCH_START
                  const batchIdx = out.findIndex(l => /^\s*BATCH_START\b/i.test(l||''));
                  let insertAt = 0;
                  if (batchIdx >= 0) insertAt = batchIdx; else insertAt = 0;
                  out.splice(insertAt, 0, `${pathInLine}`);
                  // ensure exactly one blank line after inserted line
                  if (out[insertAt+1] && out[insertAt+1].trim() !== '') { out.splice(insertAt+1, 0, ''); }
                  return out.join('\n');
                });
                setToast({ show:true, type:'success', message:'Uploaded input and updated PATH_IN' });
              } else {
                setToast({ show:true, type:'warning', message:'Upload succeeded but no path returned' });
              }
            } catch (err) {
              setToast({ show:true, type:'error', message:'Upload failed' });
            }
          }} />
        </div>
      )}

      {/* Run */}
      <div style={{ position:'relative' }}>
        <button className="docs-tab" onClick={() => setOpenMenu(m => m==='run'?null:'run')}>Run</button>
        {openMenu === 'run' && (
          <div style={{ position:'absolute', top:'100%', left:0, background:'#0b1020', border:'1px solid #1f2937', minWidth:200, zIndex:5 }} onMouseLeave={()=>setOpenMenu(null)}>
            <button 
              className="docs-tab" 
              style={{ 
                display:'flex', 
                width:'100%', 
                justifyContent:'space-between',
                opacity: canAccessFeature('BAIQL_EXECUTE') ? 1 : 0.6
              }} 
              onClick={()=>{ 
                setOpenMenu(null); 
                handleExecute(); 
              }}
              title={!canAccessFeature('BAIQL_EXECUTE') ? getFeatureDenialMessage('BAIQL_EXECUTE') : 'Execute script'}
            >
              <span>Execute</span><span style={{ color:'#9ca3af' }}>Ctrl+Enter</span>
            </button>
            <button 
              className="docs-tab" 
              style={{ 
                display:'flex', 
                width:'100%', 
                justifyContent:'space-between',
                borderTop: '1px solid #1f2937'
              }} 
              onClick={(e)=>{ 
                e.preventDefault();
                e.stopPropagation();
                setOpenMenu(null);
                // Small delay to ensure menu closes before modal opens
                setTimeout(() => {
                  setShowDownloadModal(true);
                }, 100);
              }}
              title="Download PocketPE Agent"
            >
              <span>Download Agent</span>
            </button>
          </div>
        )}
      </div>

      {/* Script name input */}
      <input
        type="text"
        value={scriptName}
        onChange={(e)=>{ setScriptName(e.target.value); setDirty(true); }}
        onKeyDown={(e)=>{ if (e.key === 'Enter') { e.preventDefault(); handleSave(); } }}
        placeholder="Type Script  Name here"
        title="Script name"
        style={{ marginLeft:12, minWidth:240, maxWidth:380, padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }}
      />

      <div style={{ marginLeft:'auto', fontSize:12, color:'#9ca3af' }}>
        <span title="Current script name" style={{ marginRight: 12 }}>{dirty ? `* ${scriptName}` : scriptName}{lastSaved ? ` • Saved ${lastSaved.toLocaleTimeString()}` : ''}</span>
        <span style={{ marginRight:12 }}>Mode: {uiMode}</span>
        {status}
      </div>
    </div>
  ), [openMenu, handleNew, handleLoad, handleClear, handleExecute, setShowTemplates, dirty, scriptName, lastSaved, status, uiMode, triggerEditor, canAccessFeature, getFeatureDenialMessage]);

  const toolbar = menuBar;

  // Free users can access BAIQL editor - they just can't execute
  // The execution check happens in handleExecute
  return (
    <div className="admin-container baiql-editor">
      <BAIQLCommandPanel userTier={tierInfo.tier.toLowerCase()} isScriptEditor={true} />
      <div className="admin-section">
        <h2 className="admin-header">
          <img 
            src={uiMode === 'Basic' ? '/blocks.png' : '/code.png'} 
            alt={uiMode === 'Basic' ? 'BAIQL Blocks' : 'BAIQL Code'} 
            style={{ height: '50px', width: 'auto' }}
          />
        </h2>
        {toolbar}
        {uiMode === 'Basic' ? (
          <MiniBAIQL
            miniState={miniState}
            onMiniChange={(patch) => setMiniState(prev => ({ ...prev, ...(patch||{}) }))}
            isPro={isPro}
            onGenerateCode={(payload) => {
              try {
                // Normalize indentation for lines inside BATCH blocks (3 spaces)
                const normalizeBatchIndent = (script) => {
                  try {
                    const lines = String(script || '').split(/\r?\n/);
                    let depth = 0;
                    for (let i = 0; i < lines.length; i++) {
                      const raw = lines[i] == null ? '' : String(lines[i]);
                      const isBatchStart = /^\s*BATCH_START\b/i.test(raw);
                      const isBatchEnd = /^\s*END_BATCH\b/i.test(raw);
                      // Do not indent the control lines themselves
                      if (isBatchEnd) {
                        depth = Math.max(0, depth - 1);
                      }
                      if (!isBatchStart && !isBatchEnd) {
                        if (depth > 0) {
                          if (raw.trim() !== '') {
                            const stripped = raw.replace(/^\s+/, '');
                            lines[i] = `   ${stripped}`; // exactly 3 spaces
                          }
                        } else {
                          // outside batch: leave as-is
                        }
                      }
                      if (isBatchStart) {
                        depth += 1;
                      }
                    }
                    return lines.join('\n');
                  } catch {
                    return String(script || '');
                  }
                };
                // Ensure a trailing END_BATCH if there are more starts than ends
                const ensureBalancedBatches = (script) => {
                  try {
                    const lines = String(script || '').split(/\r?\n/);
                    const isBlank = (v) => v == null || String(v).trim() === '';
                    let starts = 0, ends = 0;
                    for (let i = 0; i < lines.length; i++) {
                      const t = lines[i] || '';
                      if (/^\s*BATCH_START\b/i.test(t)) starts++;
                      if (/^\s*END_BATCH\b/i.test(t)) ends++;
                    }
                    if (starts > ends) {
                      // Trim trailing blanks
                      while (lines.length > 0 && isBlank(lines[lines.length - 1])) lines.pop();
                      if (lines.length > 0) lines.push('');
                      lines.push('END_BATCH');
                      lines.push('');
                    }
                    return lines.join('\n');
                  } catch {
                    return String(script || '');
                  }
                };
                // Handle typed PROMPT insertion from Mini‑BAIQL
                if (payload && payload.type === 'PROMPT') {
                  const sanitizePrompt = (text) => {
                    if (text == null) return '';
                    let t = String(text).replace(/\r?\n/g, ' ').trim();
                    // strip surrounding quotes or slashes repeatedly
                    const leftChars = new Set(['"', "'", '“', '”', '‘', '’', '/', '\\']);
                    const rightChars = leftChars;
                    while (t.length > 0 && leftChars.has(t[0])) t = t.slice(1).trimStart();
                    while (t.length > 0 && rightChars.has(t[t.length - 1])) t = t.slice(0, -1).trimEnd();
                    // escape inner quotes
                    t = t.replace(/"/g, '\\"');
                    return t;
                  };
                  const mergePromptBeforeRun = (existingScript, promptLine) => {
                    const lines = String(existingScript || '').split(/\r?\n/);
                    const isBlank = (v) => v == null || String(v).trim() === '';
                    // Find first RUN
                    let runIdx = -1;
                    for (let i = 0; i < lines.length; i++) {
                      if (/^\s*RUN\b/i.test(lines[i])) { runIdx = i; break; }
                    }
                    if (runIdx >= 0) {
                      const out = [...lines];
                      // If line before RUN is PROMPT, replace it; also ensure blank before and one blank after PROMPT
                      if (runIdx - 1 >= 0 && /^\s*PROMPT\b/i.test(out[runIdx - 1] || '')) {
                        if (runIdx - 2 >= 0 && !isBlank(out[runIdx - 2])) { out.splice(runIdx - 1, 0, ''); runIdx++; }
                        out[runIdx - 1] = promptLine;
                        if (!isBlank(out[runIdx])) out.splice(runIdx, 0, '');
                        return out.join('\n');
                      }
                      // Insert PROMPT just before RUN; ensure blank line before PROMPT and one blank after
                      let insertAt = runIdx;
                      if (insertAt - 1 >= 0 && !isBlank(out[insertAt - 1])) { out.splice(insertAt, 0, ''); insertAt++; runIdx++; }
                      out.splice(insertAt, 0, promptLine);
                      if (!isBlank(out[insertAt + 1])) out.splice(insertAt + 1, 0, '');
                      return out.join('\n');
                    }

                    // If RUN missing, try first OUTPUT fallback
                    let outputIdx = -1;
                    for (let i = 0; i < lines.length; i++) {
                      if (/^\s*OUTPUT\b/i.test(lines[i])) { outputIdx = i; break; }
                    }
                    if (outputIdx >= 0) {
                      const out = [...lines];
                      // If line before OUTPUT is PROMPT, replace it; ensure RUN after PROMPT and one blank before OUTPUT
                      if (outputIdx - 1 >= 0 && /^\s*PROMPT\b/i.test(out[outputIdx - 1] || '')) {
                        if (outputIdx - 2 >= 0 && !isBlank(out[outputIdx - 2])) { out.splice(outputIdx - 1, 0, ''); outputIdx++; }
                        out[outputIdx - 1] = promptLine;
                        if (!/^\s*RUN\b/i.test(out[outputIdx])) { out.splice(outputIdx, 0, 'RUN'); outputIdx++; }
                        if (!isBlank(out[outputIdx])) out.splice(outputIdx, 0, '');
                        return out.join('\n');
                      }
                      // Insert PROMPT before OUTPUT with spacing, then RUN, then blank before OUTPUT
                      let pIdx = outputIdx;
                      if (pIdx - 1 >= 0 && !isBlank(out[pIdx - 1])) { out.splice(pIdx, 0, ''); pIdx++; outputIdx++; }
                      out.splice(pIdx, 0, promptLine); pIdx++; outputIdx++;
                      if (!/^\s*RUN\b/i.test(out[pIdx])) { out.splice(pIdx, 0, 'RUN'); pIdx++; outputIdx++; }
                      if (!isBlank(out[pIdx])) out.splice(pIdx, 0, '');
                      return out.join('\n');
                    }

                    // Fallback placement (no RUN/OUTPUT): after last MODEL or LOAD/BATCH_START or append
                    let lastModel = -1, lastLoadOrBatch = -1;
                    for (let i = 0; i < lines.length; i++) {
                      const t = lines[i] || '';
                      if (/^(?:\s*GLOBAL\s+)?MODEL\b/i.test(t)) lastModel = i;
                      if (/^\s*(LOAD|BATCH_START)\b/i.test(t)) lastLoadOrBatch = i;
                    }
                    const out = [...lines];
                    const insertAfter = (idx) => {
                      let insertAt = idx + 1;
                      if (insertAt - 1 >= 0 && !isBlank(out[insertAt - 1])) { out.splice(insertAt, 0, ''); insertAt++; }
                      out.splice(insertAt, 0, promptLine);
                      if (out[insertAt + 1] && !isBlank(out[insertAt + 1])) out.splice(insertAt + 1, 0, '');
                    };
                    if (lastModel >= 0) { insertAfter(lastModel); return out.join('\n'); }
                    if (lastLoadOrBatch >= 0) { insertAfter(lastLoadOrBatch); return out.join('\n'); }
                    const needsBlank = out.length > 0 && !isBlank(out[out.length - 1]);
                    return [...out, ...(needsBlank ? [''] : []), '', promptLine, '',].join('\n');
                  };

                  let body = sanitizePrompt(payload.rawPromptText || '');
                  // If batched and prompt lacks {input}, prefix helper sentence
                  try {
                    const isBatched = /(^|\n)\s*(LOAD|BATCH_START)\b/i.test(code || '') && /(^|\n)\s*RUN\b/i.test(code || '');
                    const hasInputToken = /\{\s*input\s*\}/i.test(body);
                    if (isBatched && !hasInputToken) {
                      body = `Use the following information to evaluate the prompt: {input}. ${body}`;
                      setToast({ show:true, type:'warning', message:'Prompt adjusted for performance.' });
                    }
                  } catch {}
                  if (!body) { setToast({ show:true, type:'warning', message:'Prompt is empty' }); return; }
                  const promptLine = `PROMPT "${body}"${payload.optimize ? ' OPTIMIZE' : ''}`;
                  setCode(prev => ensureBalancedBatches(normalizeBatchIndent(mergePromptBeforeRun(prev, promptLine))));
                  setToast({ show:true, type:'success', message:'Inserted PROMPT' });
                  return;
                }

                // Handle CHECKPOINT insertion/removal (surgical)
                if (payload && payload.type === 'CHECKPOINT') {
                  const enabled = !!payload.enabled;
                  const everyRaw = parseInt(payload.every, 10);
                  const every = Math.max(2, Math.min(isNaN(everyRaw) ? 10 : everyRaw, 100));
                  const secsRaw = parseInt(payload.seconds, 10);
                  const seconds = Math.max(30, Math.min(isNaN(secsRaw) ? 300 : secsRaw, 1800));
                  const messageIn = typeof payload.message === 'string' ? payload.message : undefined;
                  const message = (messageIn && messageIn.trim().length > 0) ? messageIn.trim() : undefined;

                  const mergeCheckpoint = (existingScript) => {
                    const lines = String(existingScript || '').split(/\r?\n/);
                    const out = [...lines];
                    const isBlank = (v) => v == null || String(v).trim() === '';

                    // Remove existing CHECKPOINT lines; collapse at most one adjacent blank
                    for (let i = out.length - 1; i >= 0; i--) {
                      if (/^\s*CHECKPOINT\b/i.test(out[i] || '')) {
                        out.splice(i, 1);
                        if (i < out.length && isBlank(out[i]) && i + 1 < out.length && isBlank(out[i + 1])) {
                          out.splice(i + 1, 1);
                        }
                        if (i - 1 >= 0 && isBlank(out[i - 1]) && i < out.length && isBlank(out[i])) {
                          out.splice(i, 1);
                        }
                      }
                    }
                    if (!enabled) return out.join('\n');

                    // Insertion order: after DEADLINE, after TIMEOUT, after ERROR, after LIMIT, before BATCH_START, after PATH_OUT, top
                    let insertAt = 0;
                    const idxDeadline = out.findIndex(l => /^\s*DEADLINE\b/i.test(l || ''));
                    if (idxDeadline >= 0) {
                      insertAt = idxDeadline + 1;
                    } else {
                      const idxTimeout = out.findIndex(l => /^\s*TIMEOUT\b/i.test(l || ''));
                      if (idxTimeout >= 0) {
                        insertAt = idxTimeout + 1;
                      } else {
                        const idxError = out.findIndex(l => /^\s*(?:\s*GLOBAL\s+)?ERROR\b/i.test(l || ''));
                        if (idxError >= 0) {
                          insertAt = idxError + 1;
                        } else {
                          const idxLimit = out.findIndex(l => /^(?:\s*GLOBAL\s+)?LIMIT\b/i.test(l || ''));
                          if (idxLimit >= 0) {
                            insertAt = idxLimit + 1;
                          } else {
                            const idxBatchStart = out.findIndex(l => /^\s*BATCH_START\b/i.test(l || ''));
                            if (idxBatchStart >= 0) {
                              insertAt = idxBatchStart;
                            } else {
                              const idxPathOut = out.findIndex(l => /^(?:\s*GLOBAL\s+)?PATH_OUT\b/i.test(l || ''));
                              if (idxPathOut >= 0) insertAt = idxPathOut + 1; else insertAt = 0;
                            }
                          }
                        }
                      }
                    }

                    // Ensure exactly one blank before
                    if (insertAt > 0 && !isBlank(out[insertAt - 1])) {
                      out.splice(insertAt, 0, '');
                      insertAt += 1;
                    }
                    if (insertAt === 0) {
                      out.splice(0, 0, '');
                      insertAt = 1;
                    }

                    // Build CHECKPOINT line
                    const parts = ['CHECKPOINT'];
                    if (Number.isFinite(every)) parts.push(`EVERY ${every}`);
                    if (Number.isFinite(seconds)) parts.push(`TIMEOUT ${seconds}`);
                    if (typeof message === 'string' && message.length > 0) {
                      // escape inner quotes
                      const msgEsc = message.replace(/"/g, '\\"');
                      parts.push(`MESSAGE "${msgEsc}"`);
                    }
                    const line = parts.join(' ');
                    out.splice(insertAt, 0, line);

                    // Ensure exactly one blank after
                    const afterIdx = insertAt + 1;
                    if (!(afterIdx < out.length && isBlank(out[afterIdx]))) {
                      out.splice(afterIdx, 0, '');
                    }

                    return out.join('\n');
                  };

                  setCode(prev => mergeCheckpoint(prev));
                  setToast({ show:true, type:'success', message: enabled ? 'Inserted Controls' : 'Removed CHECKPOINT' });
                  return;
                }
                // Handle in-app toast requests from Mini‑BAIQL
                if (payload && payload.type === 'TOAST') {
                  const level = (payload.level || 'info');
                  const map = { error:'error', warning:'warning', success:'success', info:'info' };
                  setToast({ show:true, type: map[level] || 'info', message: String(payload.message || '') });
                  return;
                }

                // Handle LOAD(S) insertion/merge from Mini‑BAIQL
                if (payload && payload.type === 'PREPROCESS_LOADS') {
                  const isBlank = (v) => v == null || String(v).trim() === '';
                  const incomingPathIn = (payload.pathInLine && String(payload.pathInLine).trim()) ? String(payload.pathInLine) : '';
                  const targets = Array.isArray(payload.targets) ? payload.targets : [];
                  const csvCfg = payload.csv || { mode:'header', names:'' };

                  const buildPhase1 = () => {
                    const lines = [];
                    if (incomingPathIn) { lines.push(incomingPathIn); lines.push(''); }
                    lines.push('BATCH_START QUIET');
                    lines.push('');
                    const summarizePrompt = (isCsv) => {
                      const headerNote = String(csvCfg.mode||'header').toLowerCase()==='noheader'
                        ? (String(csvCfg.names||'').trim() ? `No header. Use explicit column names: ${String(csvCfg.names).trim()}.` : 'No header present; infer names col1..colN.')
                        : 'Assume header row present.';
                      return `PROMPT "From the CSV below, return a compact JSON summary with: file:{filename}, rows, cols, schema:[{name,type}], stats:{ <col>:{min,max,mean,missing,distinct} }, samples:{ <col>:{first:[3], last:[3]} }. ${headerNote} Return only JSON. CSV:\n\n{input}"`;
                    };
                    for (const t of targets) {
                      const pat = String(t.pattern||'').trim(); if (!pat) continue;
                      lines.push(`LOAD "${pat}" AS WORK`);
                      lines.push(summarizePrompt(!!t.isCsv));
                      lines.push('RUN');
                      lines.push('PATH_OUT summaries');
                      lines.push('OUTPUT "{filename}.json" FORMAT json SEPARATE');
                      lines.push('');
                    }
                    lines.push('END_BATCH');
                    lines.push('');
                    return lines.join('\n');
                  };

                  const repointPhase2 = (existingScript) => {
                    const lines = String(existingScript || '').split(/\r?\n/);
                    const out = [...lines];
                    // Remove existing PATH_IN and insert incoming at top
                    if (incomingPathIn) {
                      for (let j = out.length - 1; j >= 0; j--) { if (/^(?:\s*GLOBAL\s+)?PATH_IN\b/i.test(out[j]||'')) out.splice(j,1); }
                      out.splice(0,0,incomingPathIn);
                      if (out[1] && !isBlank(out[1])) out.splice(1,0,'');
                      while (out[2]!=null && isBlank(out[2])) out.splice(2,1);
                    }
                    // Ensure a BATCH_START
                    let bs = -1; for (let i=0;i<out.length;i++){ if (/^\s*BATCH_START\b/i.test(out[i]||'')){ bs=i; break; } }
                    if (bs < 0) {
                      while (out.length>0 && isBlank(out[out.length-1])) out.pop();
                      if (out.length>0) out.push('');
                      out.push('BATCH_START EXECUTE');
                      out.push('');
                      out.push('LOAD "summaries\\*.json" AS FILE');
                      out.push('');
                      return out.join('\n');
                    }
                    // Replace load region with summaries load
                    let stop = bs+1;
                    while (stop < out.length) {
                      const t = out[stop]||'';
                      if (/^\s*(GLOBAL\s+)?MODEL\b/i.test(t)) break;
                      if (/^\s*PROMPT\b/i.test(t)) break;
                      if (/^\s*RUN\b/i.test(t)) break;
                      if (/^\s*OUTPUT\b/i.test(t)) break;
                      if (/^\s*END_BATCH\b/i.test(t)) break;
                      if (/^\s*BATCH_START\b/i.test(t)) break;
                      stop++;
                    }
                    const newSub = ['','LOAD "summaries\\*.json" AS FILE',''];
                    out.splice(bs+1, Math.max(0, stop-(bs+1)), ...newSub);
                    return out.join('\n');
                  };

                  const phase1 = buildPhase1();
                  const phase2 = repointPhase2(code);
                  const combined = [phase1, normalizeBatchIndent(phase2)].join('\n');
                  setCode(ensureBalancedBatches(combined));
                  setToast({ show:true, type:'success', message:'Added preprocessing for CSV directory (summaries + compare).' });
                  return;
                }
                if (payload && payload.type === 'LOADS') {
                  const isBlank = (v) => v == null || String(v).trim() === '';
                  const sanitize = (s) => {
                    if (s == null) return '';
                    return String(s).trim().replace(/^"|"$/g, '').replace(/\//g, '\\');
                  };
                  const incomingPathIn = (payload.pathInLine && String(payload.pathInLine).trim()) ? String(payload.pathInLine) : '';
                  const incomingLoads = Array.isArray(payload.loadLines) ? payload.loadLines.map(l => String(l)) : [];

                  const mergeLoads = (existingScript) => {
                    const lines = String(existingScript || '').split(/\r?\n/);
                    const out = [...lines];

                    // 1) Place/replace PATH_IN at the top; remove duplicates elsewhere
                    if (incomingPathIn) {
                      for (let j = out.length - 1; j >= 0; j--) {
                        if (/^(?:\s*GLOBAL\s+)?PATH_IN\b/i.test(out[j] || '')) out.splice(j, 1);
                      }
                      // Insert at top with exactly one blank line after
                      out.splice(0, 0, incomingPathIn);
                      // ensure exactly one blank after PATH_IN; OCR SET placement stays with corresponding LOAD lines
                      if (out[1] && !isBlank(out[1])) out.splice(1, 0, '');
                      while (out[2] != null && isBlank(out[2])) out.splice(2, 1);
                    }

                    // 2) Merge LOAD/OCR block after the first BATCH_START
                    let bs = -1;
                    for (let i = 0; i < out.length; i++) {
                      if (/^\s*BATCH_START\b/i.test(out[i] || '')) { bs = i; break; }
                    }
                    if (bs < 0) {
                      // No batch yet: append a new batch with loads
                      // Trim trailing blanks
                      while (out.length > 0 && isBlank(out[out.length - 1])) out.pop();
                      if (out.length > 0) out.push('');
                      out.push(String(payload.batchStartLine || 'BATCH_START'));
                      if (incomingLoads.length) {
                        out.push('');
                        out.push(...incomingLoads);
                        out.push('');
                      }
                      return out.join('\n');
                    }

                    // Find the end of the load/OCR region to replace: stop before MODEL/PROMPT/RUN/OUTPUT/END_BATCH or next BATCH_START
                    // Update existing BATCH_START line with requested mode (if provided)
                    if (payload.batchStartLine) {
                      out[bs] = String(payload.batchStartLine);
                    }
                    let stop = bs + 1;
                    while (stop < out.length) {
                      const t = out[stop] || '';
                      if (/^\s*(GLOBAL\s+)?MODEL\b/i.test(t)) break;
                      if (/^\s*PROMPT\b/i.test(t)) break;
                      if (/^\s*RUN\b/i.test(t)) break;
                      if (/^\s*OUTPUT\b/i.test(t)) break;
                      if (/^\s*END_BATCH\b/i.test(t)) break;
                      if (/^\s*BATCH_START\b/i.test(t)) break;
                      stop++;
                    }
                    // Replace region with a single blank line + loads + trailing blank line
                    const newSub = [];
                    if (incomingLoads.length) {
                      newSub.push('');
                      newSub.push(...incomingLoads);
                      newSub.push('');
                    }
                    const replaceStart = bs + 1;
                    const replaceLen = Math.max(0, stop - (bs + 1));
                    out.splice(replaceStart, replaceLen, ...newSub);

                    // Normalize: ensure OCR SET lines immediately follow their corresponding LOAD line
                    // Pattern: LOAD ... AS DIR|WORK for PDFs should be followed by SET OCR_IF_EMPTY and SET OCR_LANGS without blanks between
                    const isLoadLine = (txt) => /^\s*LOAD\b/i.test(txt || '');
                    const isSetIfEmpty = (txt) => /^\s*SET\s+OCR_IF_EMPTY\s*=\s*1\b/i.test(txt || '');
                    const isSetLangs = (txt) => /^\s*SET\s+OCR_LANGS\b/i.test(txt || '');
                    // Scan only the newly inserted sub-block region
                    let subStart = replaceStart;
                    let subEnd = replaceStart + newSub.length;
                    for (let i = subStart; i < subEnd; i++) {
                      if (!isLoadLine(out[i])) continue;
                      // compress any blanks between LOAD and next non-blank
                      while ((i + 1) < out.length && (out[i + 1] != null) && /^\s*$/.test(out[i + 1])) {
                        // But only remove the blank if the following is a SET line
                        if ((i + 2) < out.length && (isSetIfEmpty(out[i + 2]) || isSetLangs(out[i + 2]))) {
                          out.splice(i + 1, 1);
                          subEnd--;
                          continue;
                        }
                        break;
                      }
                      // If the next lines are the two SET lines but separated by blanks, remove those blanks
                      if ((i + 1) < out.length && isSetIfEmpty(out[i + 1])) {
                        // ensure next is SET LANGS directly
                        if ((i + 2) < out.length && /^\s*$/.test(out[i + 2]) && (i + 3) < out.length && isSetLangs(out[i + 3])) {
                          out.splice(i + 2, 1);
                          subEnd--;
                        }
                      } else if ((i + 1) < out.length && isSetLangs(out[i + 1])) {
                        // ensure previous is SET IF EMPTY directly; if a blank is between, remove it
                        if ((i + 2) < out.length && /^\s*$/.test(out[i + 2]) && (i + 3) < out.length && isSetIfEmpty(out[i + 3])) {
                          out.splice(i + 2, 1);
                          subEnd--;
                        }
                      }
                    }

                    return out.join('\n');
                  };

                  setCode(prev => ensureBalancedBatches(normalizeBatchIndent(mergeLoads(prev))));
                  setToast({ show:true, type:'success', message:'Inserted LOAD(s)' });
                  return;
                }

                // Handle END insertion: append END_BATCH with spacing, preserve existing script
                if (payload && payload.type === 'END') {
                  const insertEnd = (existingScript) => {
                    const lines = String(existingScript || '').split(/\r?\n/);
                    const isBlank = (v) => v == null || String(v).trim() === '';
                    // Count starts/ends
                    let starts = 0, ends = 0;
                    for (let i = 0; i < lines.length; i++) {
                      const t = lines[i] || '';
                      if (/^\s*BATCH_START\b/i.test(t)) starts++;
                      if (/^\s*END_BATCH\b/i.test(t)) ends++;
                    }
                    if (starts > ends) {
                      // Trim trailing blanks
                      while (lines.length > 0 && isBlank(lines[lines.length - 1])) lines.pop();
                      // Ensure one blank line before and add END_BATCH and trailing blank
                      if (lines.length > 0) lines.push('');
                      lines.push('END_BATCH');
                      lines.push('');
                      return lines.join('\n');
                    }
                    return String(existingScript || '');
                  };
                  setCode(prev => normalizeBatchIndent(insertEnd(prev)));
                  setToast({ show:true, type:'success', message:'Inserted END_BATCH' });
                  return;
                }

                // Handle OUTPUT insertion/merge from Mini‑BAIQL
                if (payload && payload.type === 'OUTPUT') {
                  const sanitize = (s) => {
                    if (s == null) return '';
                    return String(s).trim().replace(/^"|"$/g, '').replace(/\//g, '\\');
                  };

                  const hasExtension = (p) => {
                    const lastSep = Math.max(p.lastIndexOf('\\'), p.lastIndexOf('/'));
                    const lastDot = p.lastIndexOf('.');
                    return lastDot > lastSep;
                  };

                  const ensureSequencerForSeparate = (base, printFmt) => {
                    if (!/\bSEPARATE\b/i.test(printFmt || '')) return base;
                    const hasSeq = /\{#.+?#\}|\{filename\}/i.test(base);
                    if (hasSeq) return base;
                    const lastSep = Math.max(base.lastIndexOf('\\'), base.lastIndexOf('/'));
                    const lastDot = base.lastIndexOf('.');
                    if (lastDot > lastSep) {
                      return `${base.slice(0, lastDot)}_{#01#}${base.slice(lastDot)}`;
                    }
                    return `${base}_{#01#}`;
                  };

                  // Build final OUTPUT destination with extension + sequencer rules
                  let dest = sanitize(payload.dest || '');
                  const ft = (payload.fileType || '').trim();
                  const pf = (payload.printFormat || '').trim();
                  if (dest) {
                    if (ft && !hasExtension(dest)) dest = `${dest}.${ft}`;
                    const beforeSeq = dest;
                    dest = ensureSequencerForSeparate(dest, pf);
                    if (dest !== beforeSeq && /\bSEPARATE\b/i.test(pf || '')) {
                      try { setToast({ show:true, type:'success', message:'Added _{#01#} for SEPARATE', duration:1200 }); } catch {}
                    }
                  }

                  const xact = payload.xact ? ' XACT' : '';
                  const fmt = ft ? ` FORMAT ${ft}` : '';
                  const pfmt = pf ? ` ${pf}` : '';
                  const outputLine = dest ? `OUTPUT "${dest}"${fmt}${pfmt}${xact}` : '';
                  const incomingPathOut = (payload.pathOut && String(payload.pathOut).trim()) ? `${payload.pathOutGlobal ? 'GLOBAL ' : ''}PATH_OUT "${sanitize(payload.pathOut)}"` : '';

                  if (!outputLine && !incomingPathOut) { setToast({ show:true, type:'warning', message:'Provide PATH_OUT or Destination' }); return; }

                  const mergeOutput = (existingScript) => {
                    const isBlank = (v) => v == null || String(v).trim() === '';
                    const lines = String(existingScript || '').split(/\r?\n/);
                    let pathInIdx = -1, batchStartIdx = -1, runIdx = -1, outputIdx = -1;
                    const pathOutIndices = [];
                    for (let i = 0; i < lines.length; i++) {
                      const t = lines[i] || '';
                      if (pathInIdx < 0 && /^(?:\s*GLOBAL\s+)?PATH_IN\b/i.test(t)) pathInIdx = i;
                      if (batchStartIdx < 0 && /^\s*BATCH_START\b/i.test(t)) batchStartIdx = i;
                      if (runIdx < 0 && /^\s*RUN\b/i.test(t)) runIdx = i;
                      if (outputIdx < 0 && /^\s*OUTPUT\b/i.test(t)) outputIdx = i;
                      if (/^(?:\s*GLOBAL\s+)?PATH_OUT\b/i.test(t)) pathOutIndices.push(i);
                    }

                    const out = [...lines];

                    const ensureExactlyOneBlankBeforeAfter = (arr, idxRef) => {
                      let idx = idxRef;
                      if (idx - 1 < 0 || !isBlank(arr[idx - 1])) { arr.splice(idx, 0, ''); idx++; }
                      while (idx - 2 >= 0 && isBlank(arr[idx - 1]) && isBlank(arr[idx - 2])) { arr.splice(idx - 1, 1); idx--; }
                      if (!isBlank(arr[idx + 1])) { arr.splice(idx + 1, 0, ''); }
                      while ((idx + 2) < arr.length && isBlank(arr[idx + 2])) { arr.splice(idx + 2, 1); }
                      return idx;
                    };

                    const placePathOut = (textToPlace) => {
                      if (!textToPlace) return;
                      for (let j = out.length - 1; j >= 0; j--) {
                        if (/^(?:\s*GLOBAL\s+)?PATH_OUT\b/i.test(out[j] || '')) out.splice(j, 1);
                      }
                      let insertAt = 0;
                      if (pathInIdx >= 0) insertAt = pathInIdx + 1; else if (batchStartIdx >= 0) insertAt = batchStartIdx; else insertAt = 0;
                      out.splice(insertAt, 0, textToPlace);
                      const normalizedIdx = ensureExactlyOneBlankBeforeAfter(out, insertAt);
                      if (normalizedIdx === 0) { out.splice(0, 0, ''); }
                    };

                    let existingPathOutText = '';
                    if (pathOutIndices.length > 0) { existingPathOutText = lines[pathOutIndices[0]] || ''; }
                    const pathOutText = incomingPathOut || existingPathOutText;
                    if (pathOutText) placePathOut(pathOutText);

                    // Determine batch region (first batch)
                    let firstBatchStart = -1, firstEndBatch = -1;
                    for (let i = 0; i < out.length; i++) {
                      if (firstBatchStart < 0 && /^\s*BATCH_START\b/i.test(out[i] || '')) firstBatchStart = i;
                      if (firstBatchStart >= 0 && /^\s*END_BATCH\b/i.test(out[i] || '')) { firstEndBatch = i; break; }
                    }
                    const regionStart = firstBatchStart >= 0 ? firstBatchStart : 0;
                    const regionEnd = firstEndBatch >= 0 ? firstEndBatch : out.length;

                    // Recompute indices within region
                    let idxPrompt = -1, idxRun = -1, idxOutput = -1, idxEnd = firstEndBatch;
                    for (let i = regionStart; i < regionEnd; i++) {
                      const t = out[i] || '';
                      if (idxPrompt < 0 && /^\s*PROMPT\b/i.test(t)) idxPrompt = i;
                      if (idxRun < 0 && /^\s*RUN\b/i.test(t)) idxRun = i;
                      if (idxOutput < 0 && /^\s*OUTPUT\b/i.test(t)) idxOutput = i;
                    }

                    if (outputLine) {
                      // Determine desired OUTPUT insertion point (just before END of first batch if present)
                      let desiredAt = (idxEnd != null && idxEnd >= 0) ? idxEnd : regionEnd;

                      // Remove any RUN lines within the batch region to avoid duplicates or misplaced runs
                      for (let i = regionEnd - 1; i >= regionStart; i--) {
                        if (/^\s*RUN\b/i.test((out[i] || ''))) {
                          out.splice(i, 1);
                          if (i < desiredAt) desiredAt--; // adjust desired position after removal
                        }
                      }

                      // Recompute prompt/end indices after removals
                      idxPrompt = -1; idxEnd = -1;
                      for (let i = regionStart; i < out.length; i++) {
                        const t = out[i] || '';
                        if (idxPrompt < 0 && /^\s*PROMPT\b/i.test(t)) idxPrompt = i;
                        if (regionStart >= 0 && idxEnd < 0 && i >= regionStart && /^\s*END_BATCH\b/i.test(t)) { idxEnd = i; break; }
                      }
                      if (idxEnd < 0) idxEnd = out.length;
                      desiredAt = idxEnd;

                      // Insert RUN before OUTPUT: after PROMPT if present, else just before desiredAt
                      let runInsertAt = (idxPrompt >= 0 && idxPrompt < desiredAt) ? (idxPrompt + 1) : desiredAt;
                      if (runInsertAt > 0 && !isBlank(out[runInsertAt - 1])) { out.splice(runInsertAt, 0, ''); runInsertAt++; }
                      out.splice(runInsertAt, 0, 'RUN');
                      // Normalize spacing around RUN
                      const normalizedRunIdx = (function(arr, at){ return ensureExactlyOneBlankBeforeAfter(arr, at); })(out, runInsertAt);

                      // Recompute desired OUTPUT insertion point (just before END, after inserting RUN)
                      idxEnd = -1;
                      for (let i = regionStart; i < out.length; i++) { if (/^\s*END_BATCH\b/i.test(out[i] || '')) { idxEnd = i; break; } }
                      desiredAt = (idxEnd >= 0) ? idxEnd : out.length;

                      // Remove any existing OUTPUT lines within region to avoid duplicates
                      for (let i = regionEnd - 1; i >= regionStart; i--) {
                        if (/^\s*OUTPUT\b/i.test((out[i] || ''))) {
                          out.splice(i, 1);
                          if (i < desiredAt) desiredAt--; // keep output before end
                        }
                      }

                      // Ensure a blank before OUTPUT and insert it just before END
                      let insertAt = desiredAt;
                      if (insertAt > 0 && !isBlank(out[insertAt - 1])) { out.splice(insertAt, 0, ''); insertAt++; }
                      out.splice(insertAt, 0, outputLine);
                      ensureExactlyOneBlankBeforeAfter(out, insertAt);
                    }

                    return out.join('\n');
                  };

                  setCode(prev => ensureBalancedBatches(normalizeBatchIndent(mergeOutput(prev))));
                  setToast({ show:true, type:'success', message:'Inserted OUTPUT' });
                  return;
                }

                // Handle LIMIT (surgical, preserve other code)
                if (payload && payload.type === 'LIMIT') {
                  const enabled = !!payload.enabled;
                  const useGlobal = !!payload.global;
                  const rawVal = parseInt(payload.value, 10);
                  const readEnterpriseCap = () => { try { return parseInt(localStorage.getItem('enterprise_limit_max') || '', 10); } catch { return NaN; } };
                  const maxCap = isPro ? 5 : (readEnterpriseCap() || 15);
                  const val = Math.max(1, Math.min(isNaN(rawVal) ? 1 : rawVal, maxCap));

                  const mergeLimit = (existingScript) => {
                    const lines = String(existingScript || '').split(/\r?\n/);
                    const out = [...lines];
                    const isBlank = (v) => v == null || String(v).trim() === '';

                    // Remove existing LIMIT lines and collapse at most one adjacent blank
                    for (let i = out.length - 1; i >= 0; i--) {
                      if (/^\s*(?:GLOBAL\s+)?LIMIT\b/i.test(out[i] || '')) {
                        out.splice(i, 1);
                        // collapse double blanks after removal (at most one)
                        if (i < out.length && isBlank(out[i]) && i + 1 < out.length && isBlank(out[i + 1])) {
                          out.splice(i + 1, 1);
                        }
                        if (i - 1 >= 0 && isBlank(out[i - 1]) && i < out.length && isBlank(out[i])) {
                          out.splice(i, 1);
                        }
                      }
                    }
                    if (!enabled) return out.join('\n');

                    // Determine insertion point: after first PATH_OUT; else before first BATCH_START; else top
                    let insertAt = 0;
                    const idxPathOut = out.findIndex(l => /^(?:\s*GLOBAL\s+)?PATH_OUT\b/i.test(l || ''));
                    if (idxPathOut >= 0) {
                      insertAt = idxPathOut + 1;
                    } else {
                      const idxBatchStart = out.findIndex(l => /^\s*BATCH_START\b/i.test(l || ''));
                      insertAt = idxBatchStart >= 0 ? idxBatchStart : 0;
                    }

                    // Ensure exactly one blank before
                    if (insertAt > 0 && !isBlank(out[insertAt - 1])) {
                      out.splice(insertAt, 0, '');
                      insertAt += 1;
                    }
                    if (insertAt === 0) {
                      out.splice(0, 0, '');
                      insertAt = 1;
                    }

                    // Insert LIMIT line
                    const limitLine = `${useGlobal ? 'GLOBAL ' : ''}LIMIT ${val}`;
                    out.splice(insertAt, 0, limitLine);

                    // Ensure exactly one blank after
                    const afterIdx = insertAt + 1;
                    if (!(afterIdx < out.length && isBlank(out[afterIdx]))) {
                      out.splice(afterIdx, 0, '');
                    }

                    return out.join('\n');
                  };

                  setCode(prev => mergeLimit(prev));
                  setToast({ show:true, type:'success', message: enabled ? 'Inserted LIMIT' : 'Removed LIMIT' });
                  return;
                }

                if (payload && typeof payload.code === 'string') {
                  const s = String(payload.code || '');
                  const nonEmpty = s.split(/\r?\n/).filter(l => l.trim().length > 0);
                  const modelLineMatch = s.match(/^(?:\s*GLOBAL\s+)?MODEL\b[^\n]*$/im);
                  // If a MODEL line is present anywhere in the code payload, prefer surgical MODEL merge
                  if (modelLineMatch) {
                    // Merge MODEL line into existing code without wiping other content
                    setCode(prev => {
                      const modelLine = modelLineMatch[0].trim();
                      const lines = String(prev || '').split(/\r?\n/);
                      let idxModel = -1;
                      let idxLastLoad = -1;
                      for (let i = 0; i < lines.length; i++) {
                        const t = lines[i];
                        if (/^(?:\s*GLOBAL\s+)?MODEL\b/i.test(t)) idxModel = i;
                        if (/^\s*LOAD\b/i.test(t)) idxLastLoad = i;
                      }
                      const ensureExactlyOneBlankBeforeAfter = (arr, idxRef) => {
                        const isBlank = (v) => v == null || String(v).trim() === '';
                        let idx = idxRef;
                        if (idx - 1 < 0 || !isBlank(arr[idx - 1])) { arr.splice(idx, 0, ''); idx++; }
                        while (idx - 2 >= 0 && isBlank(arr[idx - 1]) && isBlank(arr[idx - 2])) { arr.splice(idx - 1, 1); idx--; }
                        if (!isBlank(arr[idx + 1])) { arr.splice(idx + 1, 0, ''); }
                        while ((idx + 2) < arr.length && isBlank(arr[idx + 2])) { arr.splice(idx + 2, 1); }
                        return idx;
                      };
                      const upsertFallbackAfterModel = (arr, modelIdx) => {
                        const isBlank = (v) => v == null || String(v).trim() === '';
                        const fb = String(payload?.fallbackModel || '').trim();
                        // Locate next non-blank after model
                        let j = modelIdx + 1;
                        while (j < arr.length && isBlank(arr[j])) j++;
                        const isFallbackLine = (txt) => /^(?:\s*GLOBAL\s+)?FALLBACK\b/i.test(txt || '');
                        const useGlobal = !!(miniState && miniState.modelGlobal);
                        if (!fb || fb === 'None') {
                          if (isFallbackLine(arr[j] || '')) {
                            arr.splice(j, 1);
                            // leave spacing after MODEL as previously enforced
                          }
                          return;
                        }
                        const fallbackLine = `${useGlobal ? 'GLOBAL ' : ''}FALLBACK "${fb}"`;
                        // Ensure there is exactly one blank after MODEL before inserting fallback
                        let insertAt = modelIdx + 1;
                        if (!isBlank(arr[insertAt])) { arr.splice(insertAt, 0, ''); insertAt++; }
                        // If the next non-blank is an existing FALLBACK, replace it
                        if (isFallbackLine(arr[j] || '')) {
                          arr[j] = fallbackLine;
                          ensureExactlyOneBlankBeforeAfter(arr, j);
                          return;
                        }
                        // Otherwise insert fallback at insertAt and normalize spacing
                        arr.splice(insertAt, 0, fallbackLine);
                        ensureExactlyOneBlankBeforeAfter(arr, insertAt);
                      };
                      if (idxModel >= 0) {
                        // replace existing MODEL and ensure blank line before and after
                        if (idxModel - 1 >= 0 && lines[idxModel - 1].trim() !== '') { lines.splice(idxModel, 0, ''); idxModel++; }
                        lines[idxModel] = modelLine;
                        if (!lines[idxModel + 1] || lines[idxModel + 1].trim() !== '') { lines.splice(idxModel + 1, 0, ''); }
                        // upsert FALLBACK after MODEL
                        upsertFallbackAfterModel(lines, idxModel);
                        return lines.join('\n');
                      }
                      const out = [...lines];
                      if (idxLastLoad >= 0) {
                      // insert after last LOAD and its immediate OCR SET lines (if present)
                      const isBlank = (v) => v == null || String(v).trim() === '';
                      const isSetIfEmpty = (txt) => /^\s*SET\s+OCR_IF_EMPTY\s*=\s*1\b/i.test(txt || '');
                      const isSetLangs = (txt) => /^\s*SET\s+OCR_LANGS\b/i.test(txt || '');
                      let insertAt = idxLastLoad + 1;
                      // advance past any blanks
                      while (insertAt < out.length && isBlank(out[insertAt])) insertAt++;
                      // if following lines are SET OCR_* lines, advance past that contiguous block (ignoring intermediate blanks)
                      let j = insertAt;
                      let seenAnySet = false;
                      while (j < out.length) {
                        const t = out[j] || '';
                        if (isBlank(t)) { j++; continue; }
                        if (isSetIfEmpty(t) || isSetLangs(t)) { seenAnySet = true; j++; continue; }
                        break;
                      }
                      if (seenAnySet) insertAt = j;
                      // ensure a blank line before MODEL
                      if (out[insertAt] && !isBlank(out[insertAt])) out.splice(insertAt, 0, '');
                      const modelAt = insertAt + (out[insertAt] === '' ? 1 : 0);
                        out.splice(modelAt, 0, modelLine);
                        // ensure a blank line after MODEL
                      if (!out[modelAt + 1] || !isBlank(out[modelAt + 1])) out.splice(modelAt + 1, 0, '');
                        // upsert FALLBACK after MODEL
                        upsertFallbackAfterModel(out, modelAt);
                        return out.join('\n');
                      }
                      // fallback: append with leading blank line if needed
                      const needsBlank = out.length > 0 && out[out.length - 1].trim() !== '';
                      const appended = [...out, ...(needsBlank ? [''] : []), '', modelLine, '',];
                      // Insert fallback after appended model if requested
                      const modelAt = appended.length - 2; // index of modelLine in appended array
                      upsertFallbackAfterModel(appended, modelAt);
                      return appended.join('\n');
                    });
                    if (payload?.title) setScriptName(payload.title);
                    setToast({ show:true, type:'success', message:'Inserted MODEL' });
                    return;
                  } else {
                    // Default behavior: replace editor with generated script
                    const injectFallback = (script) => {
                      try {
                        const lines = String(script || '').split(/\r?\n/);
                        const isBlank = (v) => v == null || String(v).trim() === '';
                        const fb = String(payload?.fallbackModel || '').trim();
                        // Find first MODEL
                        let idxModel = -1;
                        for (let i = 0; i < lines.length; i++) { if (/^(?:\s*GLOBAL\s+)?MODEL\b/i.test(lines[i] || '')) { idxModel = i; break; } }
                        if (idxModel < 0) return String(script || '');
                        // If no fallback requested or explicitly None, remove any immediate fallback after MODEL and return
                        const isFallbackLine = (txt) => /^(?:\s*GLOBAL\s+)?FALLBACK\b/i.test(txt || '');
                        if (!fb || fb === 'None') {
                          // remove immediate fallback if present
                          let j = idxModel + 1; while (j < lines.length && isBlank(lines[j])) j++;
                          if (isFallbackLine(lines[j] || '')) { lines.splice(j, 1); }
                          return lines.join('\n');
                        }
                        const useGlobal = !!(miniState && miniState.modelGlobal);
                        const fallbackLine = `${useGlobal ? 'GLOBAL ' : ''}FALLBACK "${fb}"`;
                        // Ensure exactly one blank after MODEL and insert/replace fallback
                        let insertAt = idxModel + 1;
                        if (!isBlank(lines[insertAt])) { lines.splice(insertAt, 0, ''); insertAt++; }
                        let j = insertAt; while (j < lines.length && isBlank(lines[j])) j++;
                        if (isFallbackLine(lines[j] || '')) {
                          lines[j] = fallbackLine;
                          // ensure one blank after fallback
                          if (!isBlank(lines[j + 1])) lines.splice(j + 1, 0, '');
                          while ((j + 2) < lines.length && isBlank(lines[j + 2])) lines.splice(j + 2, 1);
                          return lines.join('\n');
                        }
                        lines.splice(insertAt, 0, fallbackLine);
                        if (!isBlank(lines[insertAt + 1])) lines.splice(insertAt + 1, 0, '');
                        while ((insertAt + 2) < lines.length && isBlank(lines[insertAt + 2])) lines.splice(insertAt + 2, 1);
                        return lines.join('\n');
                      } catch {
                        return String(script || '');
                      }
                    };
                    setCode(ensureBalancedBatches(normalizeBatchIndent(injectFallback(s))));
                    if (payload?.title) setScriptName(payload.title);
                    setToast({ show:true, type:'success', message:'Saved to Editor' });
                    return;
                  }
                }

                // Handle ERROR insertion/removal (surgical)
                if (payload && payload.type === 'ERROR') {
                  const enabled = !!payload.enabled;
                  const modeIn = String(payload.mode || '').toUpperCase();
                  const retriesRaw = parseInt(payload.retries, 10);
                  const retries = Math.max(1, Math.min(isNaN(retriesRaw) ? 3 : retriesRaw, 3));

                  const mergeError = (existingScript) => {
                    const lines = String(existingScript || '').split(/\r?\n/);
                    const out = [...lines];
                    const isBlank = (v) => v == null || String(v).trim() === '';

                    // Remove existing ERROR lines; collapse at most one adjacent blank
                    for (let i = out.length - 1; i >= 0; i--) {
                      if (/^\s*(?:GLOBAL\s+)?ERROR\b/i.test(out[i] || '')) {
                        out.splice(i, 1);
                        if (i < out.length && isBlank(out[i]) && i + 1 < out.length && isBlank(out[i + 1])) {
                          out.splice(i + 1, 1);
                        }
                        if (i - 1 >= 0 && isBlank(out[i - 1]) && i < out.length && isBlank(out[i])) {
                          out.splice(i, 1);
                        }
                      }
                    }
                    if (!enabled) return out.join('\n');

                    // Determine insertion point priority:
                    // 1) after LIMIT  2) before BATCH_START  3) after PATH_OUT  4) top
                    let insertAt = 0;
                    const idxLimit = out.findIndex(l => /^(?:\s*GLOBAL\s+)?LIMIT\b/i.test(l || ''));
                    if (idxLimit >= 0) {
                      insertAt = idxLimit + 1;
                    } else {
                      const idxBatchStart = out.findIndex(l => /^\s*BATCH_START\b/i.test(l || ''));
                      if (idxBatchStart >= 0) {
                        insertAt = idxBatchStart;
                      } else {
                        const idxPathOut = out.findIndex(l => /^(?:\s*GLOBAL\s+)?PATH_OUT\b/i.test(l || ''));
                        if (idxPathOut >= 0) insertAt = idxPathOut + 1; else insertAt = 0;
                      }
                    }

                    // Ensure exactly one blank before
                    if (insertAt > 0 && !isBlank(out[insertAt - 1])) {
                      out.splice(insertAt, 0, '');
                      insertAt += 1;
                    }
                    if (insertAt === 0) {
                      out.splice(0, 0, '');
                      insertAt = 1;
                    }

                    // Build ERROR line
                    let mode = modeIn;
                    if (!mode || !/^(SKIP|HALT|RETRY)$/.test(mode)) mode = 'RETRY';
                    const errorLine = mode === 'RETRY' ? `ERROR RETRY ${retries}` : `ERROR ${mode}`;

                    // Insert ERROR
                    out.splice(insertAt, 0, errorLine);

                    // Ensure exactly one blank after
                    const afterIdx = insertAt + 1;
                    if (!(afterIdx < out.length && isBlank(out[afterIdx]))) {
                      out.splice(afterIdx, 0, '');
                    }

                    return out.join('\n');
                  };

                  setCode(prev => mergeError(prev));
                  setToast({ show:true, type:'success', message: enabled ? 'Inserted ERROR' : 'Removed ERROR' });
                  return;
                }

                // Handle TIMEOUT insertion/removal (surgical)
                if (payload && payload.type === 'TIMEOUT') {
                  const enabled = !!payload.enabled;
                  const secsRaw = parseInt(payload.seconds, 10);
                  const seconds = Math.max(1, Math.min(isNaN(secsRaw) ? 60 : secsRaw, 999));
                  const modeIn = String(payload.mode || '').toUpperCase();
                  const mode = /^(SKIP|HALT|RETRY)$/.test(modeIn) ? modeIn : 'SKIP';

                  const mergeTimeout = (existingScript) => {
                    const lines = String(existingScript || '').split(/\r?\n/);
                    const out = [...lines];
                    const isBlank = (v) => v == null || String(v).trim() === '';

                    // Remove existing TIMEOUT lines; collapse at most one adjacent blank
                    for (let i = out.length - 1; i >= 0; i--) {
                      if (/^\s*TIMEOUT\b/i.test(out[i] || '')) {
                        out.splice(i, 1);
                        if (i < out.length && isBlank(out[i]) && i + 1 < out.length && isBlank(out[i + 1])) {
                          out.splice(i + 1, 1);
                        }
                        if (i - 1 >= 0 && isBlank(out[i - 1]) && i < out.length && isBlank(out[i])) {
                          out.splice(i, 1);
                        }
                      }
                    }
                    if (!enabled) return out.join('\n');

                    // Insertion order: after ERROR, after LIMIT, before BATCH_START, after PATH_OUT, top
                    let insertAt = 0;
                    const idxError = out.findIndex(l => /^\s*(?:GLOBAL\s+)?ERROR\b/i.test(l || ''));
                    if (idxError >= 0) {
                      insertAt = idxError + 1;
                    } else {
                      const idxLimit = out.findIndex(l => /^(?:\s*GLOBAL\s+)?LIMIT\b/i.test(l || ''));
                      if (idxLimit >= 0) {
                        insertAt = idxLimit + 1;
                      } else {
                        const idxBatchStart = out.findIndex(l => /^\s*BATCH_START\b/i.test(l || ''));
                        if (idxBatchStart >= 0) {
                          insertAt = idxBatchStart;
                        } else {
                          const idxPathOut = out.findIndex(l => /^(?:\s*GLOBAL\s+)?PATH_OUT\b/i.test(l || ''));
                          if (idxPathOut >= 0) insertAt = idxPathOut + 1; else insertAt = 0;
                        }
                      }
                    }

                    // Ensure exactly one blank before
                    if (insertAt > 0 && !isBlank(out[insertAt - 1])) {
                      out.splice(insertAt, 0, '');
                      insertAt += 1;
                    }
                    if (insertAt === 0) {
                      out.splice(0, 0, '');
                      insertAt = 1;
                    }

                    // Build TIMEOUT line
                    const timeoutLine = `TIMEOUT ${seconds} ${mode}`;
                    out.splice(insertAt, 0, timeoutLine);

                    // Ensure exactly one blank after
                    const afterIdx = insertAt + 1;
                    if (!(afterIdx < out.length && isBlank(out[afterIdx]))) {
                      out.splice(afterIdx, 0, '');
                    }

                    return out.join('\n');
                  };

                  setCode(prev => mergeTimeout(prev));
                  setToast({ show:true, type:'success', message: enabled ? 'Inserted Controls' : 'Removed TIMEOUT' });
                  return;
                }

                // Handle DEADLINE insertion/removal (surgical)
                if (payload && payload.type === 'DEADLINE') {
                  const enabled = !!payload.enabled;
                  const minsRaw = parseInt(payload.minutes, 10);
                  const minutes = Math.max(1, Math.min(isNaN(minsRaw) ? 60 : minsRaw, 999));
                  const modeIn = String(payload.mode || '').toUpperCase();
                  const mode = /^(HALT|PARTIAL)$/.test(modeIn) ? modeIn : 'PARTIAL';

                  const mergeDeadline = (existingScript) => {
                    const lines = String(existingScript || '').split(/\r?\n/);
                    const out = [...lines];
                    const isBlank = (v) => v == null || String(v).trim() === '';

                    // Remove existing DEADLINE lines; collapse at most one adjacent blank
                    for (let i = out.length - 1; i >= 0; i--) {
                      if (/^\s*DEADLINE\b/i.test(out[i] || '')) {
                        out.splice(i, 1);
                        if (i < out.length && isBlank(out[i]) && i + 1 < out.length && isBlank(out[i + 1])) {
                          out.splice(i + 1, 1);
                        }
                        if (i - 1 >= 0 && isBlank(out[i - 1]) && i < out.length && isBlank(out[i])) {
                          out.splice(i, 1);
                        }
                      }
                    }
                    if (!enabled) return out.join('\n');

                    // Insertion order: after TIMEOUT, after ERROR, after LIMIT, before BATCH_START, after PATH_OUT, top
                    let insertAt = 0;
                    const idxTimeout = out.findIndex(l => /^\s*TIMEOUT\b/i.test(l || ''));
                    if (idxTimeout >= 0) {
                      insertAt = idxTimeout + 1;
                    } else {
                      const idxError = out.findIndex(l => /^\s*(?:GLOBAL\s+)?ERROR\b/i.test(l || ''));
                      if (idxError >= 0) {
                        insertAt = idxError + 1;
                      } else {
                        const idxLimit = out.findIndex(l => /^(?:\s*GLOBAL\s+)?LIMIT\b/i.test(l || ''));
                        if (idxLimit >= 0) {
                          insertAt = idxLimit + 1;
                        } else {
                          const idxBatchStart = out.findIndex(l => /^\s*BATCH_START\b/i.test(l || ''));
                          if (idxBatchStart >= 0) {
                            insertAt = idxBatchStart;
                          } else {
                            const idxPathOut = out.findIndex(l => /^(?:\s*GLOBAL\s+)?PATH_OUT\b/i.test(l || ''));
                            if (idxPathOut >= 0) insertAt = idxPathOut + 1; else insertAt = 0;
                          }
                        }
                      }
                    }

                    // Ensure exactly one blank before
                    if (insertAt > 0 && !isBlank(out[insertAt - 1])) {
                      out.splice(insertAt, 0, '');
                      insertAt += 1;
                    }
                    if (insertAt === 0) {
                      out.splice(0, 0, '');
                      insertAt = 1;
                    }

                    // Build DEADLINE line
                    const deadlineLine = `DEADLINE ${minutes} ${mode}`;
                    out.splice(insertAt, 0, deadlineLine);

                    // Ensure exactly one blank after
                    const afterIdx = insertAt + 1;
                    if (!(afterIdx < out.length && isBlank(out[afterIdx]))) {
                      out.splice(afterIdx, 0, '');
                    }

                    return out.join('\n');
                  };

                  setCode(prev => mergeDeadline(prev));
                  setToast({ show:true, type:'success', message: enabled ? 'Inserted Controls' : 'Removed DEADLINE' });
                  return;
                }

                // Handle PARALLEL insertion/removal (surgical)
                if (payload && payload.type === 'PARALLEL') {
                  const enabled = !!payload.enabled;
                  const nRaw = parseInt(payload.n, 10);
                  const n = Math.max(1, Math.min(isNaN(nRaw) ? 1 : nRaw, 5));

                  const mergeParallel = (existingScript) => {
                    const lines = String(existingScript || '').split(/\r?\n/);
                    const out = [...lines];
                    const isBlank = (v) => v == null || String(v).trim() === '';

                    // Remove existing PARALLEL lines; collapse at most one adjacent blank
                    for (let i = out.length - 1; i >= 0; i--) {
                      if (/^\s*PARALLEL\b/i.test(out[i] || '')) {
                        out.splice(i, 1);
                        if (i < out.length && isBlank(out[i]) && i + 1 < out.length && isBlank(out[i + 1])) {
                          out.splice(i + 1, 1);
                        }
                        if (i - 1 >= 0 && isBlank(out[i - 1]) && i < out.length && isBlank(out[i])) {
                          out.splice(i, 1);
                        }
                      }
                    }
                    if (!enabled) return out.join('\n');

                    // Insertion order: after CHECKPOINT, after DEADLINE, after TIMEOUT, after ERROR, after LIMIT, before BATCH_START, after PATH_OUT, top
                    let insertAt = 0;
                    const idxCheckpoint = out.findAnd ? -1 : out.findIndex(l => /^\s*CHECKPOINT\b/i.test(l || '')); // ensure not shadowed
                    if (idxCheckpoint >= 0) {
                      insertAt = idxCheckpoint + 1;
                    } else {
                      const idxDeadline = out.findIndex(l => /^\s*DEADLINE\b/i.test(l || ''));
                      if (idxDeadline >= 0) {
                        insertAt = idxDeadline + 1;
                      } else {
                        const idxTimeout = out.findIndex(l => /^\s*TIMEOUT\b/i.test(l || ''));
                        if (idxTimeout >= 0) {
                          insertAt = idxTimeout + 1;
                        } else {
                          const idxError = out.findIndex(l => /^\s*(?:\s*GLOBAL\s+)?ERROR\b/i.test(l || ''));
                          if (idxError >= 0) {
                            insertAt = idxError + 1;
                          } else {
                            const idxLimit = out.findIndex(l => /^(?:\s*GLOBAL\s+)?LIMIT\b/i.test(l || ''));
                            if (idxLimit >= 0) {
                              insertAt = idxLimit + 1;
                            } else {
                              const idxBatchStart = out.findIndex(l => /^\s*BATCH_START\b/i.test(l || ''));
                              if (idxBatchStart >= 0) {
                                insertAt = idxBatchStart;
                              } else {
                                const idxPathOut = out.findIndex(l => /^(?:\s*GLOBAL\s+)?PATH_OUT\b/i.test(l || ''));
                                if (idxPathOut >= 0) insertAt = idxPathOut + 1; else insertAt = 0;
                              }
                            }
                          }
                        }
                      }
                    }

                    // Ensure exactly one blank before
                    if (insertAt > 0 && !isBlank(out[insertAt - 1])) {
                      out.splice(insertAt, 0, '');
                      insertAt += 1;
                    }
                    if (insertAt === 0) {
                      out.splice(0, 0, '');
                      insertAt = 1;
                    }

                    // Build PARALLEL line
                    const pLine = `PARALLEL ${n}`;
                    out.splice(insertAt, 0, pLine);

                    // Ensure exactly one blank after
                    const afterIdx = insertAt + 1;
                    if (!(afterIdx < out.length && isBlank(out[afterIdx]))) {
                      out.splice(afterIdx, 0, '');
                    }

                    return out.join('\n');
                  };

                  setCode(prev => mergeParallel(prev));
                  setToast({ show:true, type:'success', message: enabled ? 'Inserted Controls' : 'Removed PARALLEL' });
                  return;
                }

                // Handle TAG block insertion/removal (surgical)
                if (payload && payload.type === 'TAG') {
                  const enabled = !!payload.enabled;
                  const tagLines = Array.isArray(payload.lines) ? payload.lines.map(x => String(x).trim()).filter(Boolean) : [];

                  const mergeTagBlock = (existingScript) => {
                    const lines = String(existingScript || '').split(/\r?\n/);
                    const out = [...lines];
                    const isBlank = (v) => v == null || String(v).trim() === '';

                    // Remove all existing TAG lines; collapse at most one adjacent blank
                    for (let i = out.length - 1; i >= 0; i--) {
                      if (/^\s*TAG\b/i.test(out[i] || '')) {
                        out.splice(i, 1);
                        if (i < out.length && isBlank(out[i]) && i + 1 < out.length && isBlank(out[i + 1])) {
                          out.splice(i + 1, 1);
                        }
                        if (i - 1 >= 0 && isBlank(out[i - 1]) && i < out.length && isBlank(out[i])) {
                          out.splice(i, 1);
                        }
                      }
                    }
                    if (!enabled || tagLines.length === 0) return out.join('\n');

                    // Compute insertion point: after PARALLEL, CHECKPOINT, DEADLINE, TIMEOUT, ERROR, LIMIT, before BATCH_START, after PATH_OUT, else top
                    let insertAt = 0;
                    const idxParallel = out.findIndex(l => /^\s*PARALLEL\b/i.test(l || ''));
                    if (idxParallel >= 0) {
                      insertAt = idxParallel + 1;
                    } else {
                      const idxCheckpoint = out.findIndex(l => /^\s*CHECKPOINT\b/i.test(l || ''));
                      if (idxCheckpoint >= 0) {
                        insertAt = idxCheckpoint + 1;
                      } else {
                        const idxDeadline = out.findIndex(l => /^\s*DEADLINE\b/i.test(l || ''));
                        if (idxDeadline >= 0) {
                          insertAt = idxDeadline + 1;
                        } else {
                          const idxTimeout = out.findIndex(l => /^\s*TIMEOUT\b/i.test(l || ''));
                          if (idxTimeout >= 0) {
                            insertAt = idxTimeout + 1;
                          } else {
                            const idxError = out.findIndex(l => /^\s*(?:\s*GLOBAL\s+)?ERROR\b/i.test(l || ''));
                            if (idxError >= 0) {
                              insertAt = idxError + 1;
                            } else {
                              const idxLimit = out.findIndex(l => /^(?:\s*GLOBAL\s+)?LIMIT\b/i.test(l || ''));
                              if (idxLimit >= 0) {
                                insertAt = idxLimit + 1;
                              } else {
                                const idxBatchStart = out.findIndex(l => /^\s*BATCH_START\b/i.test(l || ''));
                                if (idxBatchStart >= 0) {
                                  insertAt = idxBatchStart;
                                } else {
                                  const idxPathOut = out.findIndex(l => /^(?:\s*GLOBAL\s+)?PATH_OUT\b/i.test(l || ''));
                                  if (idxPathOut >= 0) insertAt = idxPathOut + 1; else insertAt = 0;
                                }
                              }
                            }
                          }
                        }
                      }
                    }

                    // Ensure exactly one blank before
                    if (insertAt > 0 && !isBlank(out[insertAt - 1])) {
                      out.splice(insertAt, 0, '');
                      insertAt += 1;
                    }
                    if (insertAt === 0) {
                      out.splice(0, 0, '');
                      insertAt = 1;
                    }

                    // Insert contiguous TAG block
                    const block = [...tagLines];
                    out.splice(insertAt, 0, ...block);

                    // Ensure exactly one blank after the block
                    let after = insertAt + block.length;
                    if (!(after < out.length && isBlank(out[after]))) {
                      out.splice(after, 0, '');
                    }

                    return out.join('\n');
                  };

                  setCode(prev => mergeTagBlock(prev));
                  setToast({ show:true, type:'success', message: enabled ? 'Inserted Controls' : 'Removed TAG(s)' });
                  return;
                }
                // Placeholder handler for Controls payload (future): insert lines after BATCH_START
                if (payload && payload.type === 'CONTROLS') {
                  const controlLines = Array.isArray(payload.lines) ? payload.lines.map(x => String(x)) : [];
                  const mergeControls = (existingScript) => {
                    const isBlank = (v) => v == null || String(v).trim() === '';
                    const lines = String(existingScript || '').split(/\r?\n/);
                    const out = [...lines];
                    let bs = -1;
                    for (let i = 0; i < out.length; i++) { if (/^\s*BATCH_START\b/i.test(out[i] || '')) { bs = i; break; } }
                    if (bs < 0 || controlLines.length === 0) return existingScript;
                    // determine stop to preserve following sections
                    let stop = bs + 1;
                    while (stop < out.length) {
                      const t = out[stop] || '';
                      if (/^\s*(LOAD|GLOBAL\s+MODEL|MODEL|PROMPT|RUN|OUTPUT|END_BATCH|BATCH_START)\b/i.test(t)) break;
                      stop++;
                    }
                    const block = [''].concat(controlLines).concat(['']);
                    out.splice(bs + 1, Math.max(0, stop - (bs + 1)), ...block);
                    return out.join('\n');
                  };
                  setCode(prev => ensureBalancedBatches(normalizeBatchIndent(mergeControls(prev))));
                  setToast({ show:true, type:'success', message:'Inserted Controls' });
                  return;
                }
              } catch {}
            }}
            onSwitchToPro={() => setUiMode('Pro')}
          />
        ) : (
        <div style={panelStyles.container}>
          <div style={panelStyles.left}>
            <div className="dashboard-card" style={panelStyles.card}>
              <Suspense fallback={<div style={{ padding: 12, color:'#e5e7eb' }}>Loading editor…</div>}>
                {MonacoEditor ? (
                  <MonacoEditor
                    height="100%"
                    defaultLanguage="baiql"
                    language="baiql"
                    value={code}
                    onChange={setCode}
                    beforeMount={beforeMount}
                    onMount={onMount}
                    options={{
                      theme: 'baiql-dark',
                      fontSize: 14,
                      minimap: { enabled: false },
                      wordWrap: 'on',
                    acceptSuggestionOnEnter: 'off',
                    acceptSuggestionOnCommitCharacter: false,
                    tabCompletion: 'on',
                    suggestSelection: 'first',
                    quickSuggestions: { other: true, comments: false, strings: true },
                      scrollbar: { vertical: 'auto', horizontal: 'auto', handleMouseWheel: true },
                      smoothScrolling: false,
                      mouseWheelZoom: false,
                      cursorSmoothCaretAnimation: 'off',
                      stickyScroll: { enabled: false },
                      automaticLayout: true,
                      padding: { top: 8, bottom: 8 },
                      renderValidationDecorations: 'on'
                    }}
                  />
                ) : (
                  <textarea
                    value={code}
                    onChange={e=>setCode(e.target.value)}
                    style={{ flex:1, width:'100%', height:'100%', background:'#0b1020', color:'#e5e7eb', border:'none', padding:12, fontFamily:'ui-monospace, Menlo, Monaco, Consolas, "Courier New", monospace', fontSize:14 }}
                  />
                )}
              </Suspense>
            </div>
          </div>
          <div style={panelStyles.right}>
            <div className="dashboard-card" style={panelStyles.card}>
              <div style={{ padding: 12, borderBottom: '1px solid #1f2937', color:'#ffffff', display:'flex', alignItems:'center', gap:12, position:'sticky', top:0, background:'#0b1020', zIndex:2 }}>
                <div style={{ fontWeight:700 }}>Output / Validation</div>
                <div style={{ marginLeft:'auto', color: status.startsWith('✓') ? '#10b981' : '#ef4444' }}>{status}</div>
              </div>
              <div style={{ padding: 12, color:'#e5e7eb', background:'#0b1020', overflow:'hidden' }}>
                <div style={{ marginBottom:8 }}>
                  <strong>Detected Batch Type:</strong> <span style={{ color: (status.startsWith('✓') ? '#10b981' : '#ef4444') }}>{batchType || '—'}</span>
                </div>
                <div style={{ display:'grid', gridTemplateRows:'auto 1fr', rowGap:12, height: '100%' }}>
                  <div style={{ color:'#e5e7eb', whiteSpace:'pre-wrap', overflow:'hidden' }}>{output || '—'}</div>
                  <div className="table-scroll" style={{ borderTop:'1px solid #1f2937', overflow:'hidden' }}>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th style={{ width:80, color:'#000000' }}>Line</th>
                          <th style={{ color:'#000000' }}>Severity</th>
                          <th style={{ color:'#000000' }}>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(errors || []).length === 0 ? (
                          <tr><td colSpan={3} style={{ color:'#9ca3af' }}>No issues</td></tr>
                        ) : (
                          errors.map((e, idx) => (
                            <tr key={idx} onClick={() => { try { if (editorRef.current) { editorRef.current.revealLineInCenter(e.line || 1); editorRef.current.setPosition({ lineNumber: e.line || 1, column: 1 }); editorRef.current.focus(); } } catch {} }} style={{ cursor:'pointer' }}>
                              <td>{e.line}</td>
                              <td style={{ color: e.severity === 'warning' ? '#f59e0b' : '#ef4444' }}>{e.severity}</td>
                              <td>{e.message}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
      {showTemplates && (
        <BAIQLTemplateGallery
          isOpen={showTemplates}
          onClose={() => setShowTemplates(false)}
          onUseTemplate={(t) => {
            try { setCode(t.code || ''); setDirty(true); setShowTemplates(false); setStatus('Loaded template'); } catch { setShowTemplates(false); }
          }}
        />
      )}
      {showConfirmNew && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:30 }} onClick={()=>setShowConfirmNew(false)}>
          <div role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()} style={{ position:'absolute', top:'20%', left:'50%', transform:'translateX(-50%)', width: 'min(520px, 92vw)', background:'#0b1020', border:'1px solid #1f2937', borderRadius:8, color:'#e5e7eb' }}>
            <div style={{ display:'flex', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid #1f2937' }}>
              <div style={{ fontWeight:700, fontSize:16 }}>Discard changes?</div>
            </div>
            <div style={{ padding:16 }}>
              You have unsaved changes. Discard and create a new script?
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
                <button className="docs-tab" onClick={()=>setShowConfirmNew(false)}>Cancel</button>
                <button className="docs-tab" onClick={()=>{ setShowConfirmNew(false); performNew(); }}>Discard</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showSaveAsModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:30 }} onClick={()=>setShowSaveAsModal(false)}>
          <div role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()} style={{ position:'absolute', top:'18%', left:'50%', transform:'translateX(-50%)', width: 'min(520px, 92vw)', background:'#0b1020', border:'1px solid #1f2937', borderRadius:8, color:'#e5e7eb' }}>
            <div style={{ display:'flex', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid #1f2937' }}>
              <div style={{ fontWeight:700, fontSize:16 }}>Save As</div>
            </div>
            <div style={{ padding:16, display:'grid', gap:12 }}>
              <label htmlFor="saveAsName">Script name</label>
              <input id="saveAsName" value={saveAsName} onChange={(e)=>setSaveAsName(e.target.value)} placeholder="Enter script name" style={{ padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }} />
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button className="docs-tab" onClick={()=>setShowSaveAsModal(false)}>Cancel</button>
                <button className="docs-tab" onClick={async()=>{
                  try {
                    const name = (saveAsName || '').trim();
                    if (!name) { setToast({ show:true, type:'warning', message:'Name is required' }); return; }
                    const res = await createScript(name, code || '', null);
                    if (res && res.scriptId) {
                      setScriptId(res.scriptId);
                      setScriptName(name);
                      setLastSaved(new Date());
                      setDirty(false);
                      setStatus('✓ Saved As');
                      setShowSaveAsModal(false);
                      try { setToast({ show:true, type:'success', message:'Saved!', duration:1200 }); } catch {}
                    }
                  } catch (e) {
                    setToast({ show:true, type:'error', message:(e && e.message) ? e.message : 'Save As failed' });
                  }
                }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showRenameModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:30 }} onClick={()=>setShowRenameModal(false)}>
          <div role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()} style={{ position:'absolute', top:'22%', left:'50%', transform:'translateX(-50%)', width: 'min(520px, 92vw)', background:'#0b1020', border:'1px solid #1f2937', borderRadius:8, color:'#e5e7eb' }}>
            <div style={{ display:'flex', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid #1f2937' }}>
              <div style={{ fontWeight:700, fontSize:16 }}>Rename Script</div>
            </div>
            <div style={{ padding:16, display:'grid', gap:12 }}>
              <label htmlFor="renameName">New name</label>
              <input id="renameName" value={renameName} onChange={(e)=>setRenameName(e.target.value)} placeholder="Enter new name" style={{ padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }} />
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button className="docs-tab" onClick={()=>setShowRenameModal(false)}>Cancel</button>
                <button className="docs-tab" onClick={async()=>{
                  try {
                    const id = manageId; const name = (renameName||'').trim(); if (!id || !name) { setToast({ show:true, type:'warning', message:'Name is required' }); return; }
                    const s = await getScript(id); // fetch current content for update call
                    await updateScript(id, name, s?.content || '');
                    // refresh list
                    setScriptList(prev => (prev||[]).map(it => it.id === id ? { ...it, name, modified: new Date().toISOString() } : it));
                    setToast({ show:true, type:'success', message:'Renamed' });
                    setShowRenameModal(false);
                  } catch (e) {
                    setToast({ show:true, type:'error', message:(e && e.message) ? e.message : 'Rename failed' });
                  }
                }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDeleteConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:30 }} onClick={()=>setShowDeleteConfirm(false)}>
          <div role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()} style={{ position:'absolute', top:'24%', left:'50%', transform:'translateX(-50%)', width: 'min(480px, 92vw)', background:'#0b1020', border:'1px solid #1f2937', borderRadius:8, color:'#e5e7eb' }}>
            <div style={{ display:'flex', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid #1f2937' }}>
              <div style={{ fontWeight:700, fontSize:16 }}>Delete Script?</div>
            </div>
            <div style={{ padding:16 }}>
              This will remove the script from your library. This action cannot be undone.
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
                <button className="docs-tab" onClick={()=>setShowDeleteConfirm(false)}>Cancel</button>
                <button className="docs-tab" onClick={async()=>{
                  try {
                    const id = manageId; if (!id) { setShowDeleteConfirm(false); return; }
                    await deleteScript(id);
                    setScriptList(prev => (prev||[]).filter(it => it.id !== id));
                    setToast({ show:true, type:'success', message:'Deleted' });
                    setShowDeleteConfirm(false);
                  } catch (e) {
                    setToast({ show:true, type:'error', message:(e && e.message) ? e.message : 'Delete failed' });
                  }
                }}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showLoad && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:20 }} onClick={()=>setShowLoad(false)}>
          <div role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()} style={{ position:'absolute', top:'10%', left:'50%', transform:'translateX(-50%)', width: 'min(760px, 92vw)', background:'#0b1020', border:'1px solid #1f2937', borderRadius:8, color:'#e5e7eb', boxShadow:'0 10px 30px rgba(0,0,0,0.4)' }}>
            <div style={{ display:'flex', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid #1f2937' }}>
              <div style={{ fontWeight:700, fontSize:16 }}>Load Script</div>
              <button className="docs-tab" style={{ marginLeft:'auto' }} onClick={()=>setShowLoad(false)}>Close</button>
            </div>
            <div style={{ padding:16 }}>
              {loadingList ? (
                <div style={{ color:'#9ca3af' }}>Loading…</div>
              ) : (
                <>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 200px 200px', gap:12, alignItems:'center', marginBottom:12 }}>
                    <input
                      type="text"
                      value={loadQuery}
                      onChange={(e)=>setLoadQuery(e.target.value)}
                      placeholder="Search by name…"
                      style={{ flex:1, padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }}
                    />
                    <input
                      type="text"
                      value={meta?.authorFilter || ''}
                      onChange={(e)=>setMeta(m=>({ ...m, authorFilter: e.target.value }))}
                      placeholder="Filter by author…"
                      style={{ padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }}
                    />
                    <input
                      type="text"
                      value={meta?.versionFilter || ''}
                      onChange={(e)=>setMeta(m=>({ ...m, versionFilter: e.target.value }))}
                      placeholder="Filter by version…"
                      style={{ padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }}
                    />
                    <select
                      value={loadSort}
                      onChange={(e)=>setLoadSort(e.target.value)}
                      style={{ padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }}
                    >
                      <option value="modified_desc">Modified ↓</option>
                      <option value="modified_asc">Modified ↑</option>
                      <option value="name_asc">Name A→Z</option>
                      <option value="name_desc">Name Z→A</option>
                    </select>
                  </div>
                  <div className="table-scroll" style={{ maxHeight: '60vh', overflow:'auto', border:'1px solid #1f2937', borderRadius:6 }}>
                  <table className="admin-table" style={{ width:'100%' }}>
                    <thead>
                      <tr>
                        <th style={{ color:'#000000' }}>Name</th>
                        <th style={{ color:'#000000' }}>Modified</th>
                        <th style={{ color:'#000000' }}>Author</th>
                        <th style={{ color:'#000000' }}>Version</th>
                        <th style={{ color:'#000000', width:180 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        let items = Array.isArray(scriptList) ? [...scriptList] : [];
                        const q = (loadQuery || '').trim().toLowerCase();
                        const af = (meta?.authorFilter || '').trim().toLowerCase();
                        const vf = (meta?.versionFilter || '').trim().toLowerCase();
                        if (q) items = items.filter(it => String(it.name || '').toLowerCase().includes(q));
                        if (af) items = items.filter(it => String(it.author || '').toLowerCase().includes(af));
                        if (vf) items = items.filter(it => String(it.version || '').toLowerCase().includes(vf));
                        items.sort((a,b) => {
                          const an = String(a.name || '');
                          const bn = String(b.name || '');
                          const am = a.modified ? new Date(a.modified).getTime() : 0;
                          const bm = b.modified ? new Date(b.modified).getTime() : 0;
                          switch (loadSort) {
                            case 'name_asc': return an.localeCompare(bn);
                            case 'name_desc': return bn.localeCompare(an);
                            case 'modified_asc': return am - bm;
                            case 'modified_desc':
                            default: return bm - am;
                          }
                        });
                        if (items.length === 0) return (<tr><td colSpan={2} style={{ color:'#9ca3af' }}>No scripts found</td></tr>);
                        return items.map(item => (
                          <tr key={item.id}>
                            <td>{item.name}</td>
                            <td>{item.modified ? new Date(item.modified).toLocaleString() : ''}</td>
                            <td>{item.author || ''}</td>
                            <td>{item.version || ''}</td>
                            <td>
                              <div style={{ display:'flex', gap:8 }}>
                                <button className="docs-tab" onClick={async()=>{
                                  try {
                                    const s = await getScript(item.id);
                                    const metaResp = await getScriptMeta(item.id).catch(()=>null);
                                    setScriptId(s.id);
                                    setScriptName(s.name || 'Untitled');
                                    setCode(s.content || '');
                                    setBatchType(null);
                                    try { applyMarkers({ errors: [] }); } catch {}
                                    setErrors([]);
                                    setOutput('');
                                    setStatus(`Loaded \"${s.name}\"`);
                                    setLastSaved(new Date(s.modified || Date.now()));
                                    setDirty(false);
                                    if (metaResp) {
                                      setMeta({
                                        version: metaResp.version || '',
                                        author: metaResp.author || '',
                                        description: metaResp.description || '',
                                        changelog: metaResp.changelog || '',
                                        sharedWith: metaResp.sharedWith || '',
                                        approvalRequiredFrom: metaResp.approvalRequiredFrom || '',
                                        inheritFrom: metaResp.inheritFrom || '',
                                        emitMetaAsTags: !!metaResp.emitMetaAsTags,
                                        lockEditsWithoutApproval: !!metaResp.lockEditsWithoutApproval
                                      });
                                    }
                                    setShowLoad(false);
                                    try { localStorage.setItem('baiql_last', s.content || ''); } catch {}
                                  } catch (e) {
                                    setToast({ show:true, type:'error', message:(e && e.message) ? e.message : 'Failed to load script' });
                                  }
                                }}>Load</button>
                                <button className="docs-tab" onClick={()=>{ setManageId(item.id); setRenameName(item.name || ''); setShowRenameModal(true); }}>Rename</button>
                                <button className="docs-tab" onClick={()=>{ setManageId(item.id); setShowDeleteConfirm(true); }}>Delete</button>
                              </div>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Pairing Modal */}
      {showPairingModal && pairingCode && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:30 }} onClick={()=>setShowPairingModal(false)}>
          <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)', background:'#0b1020', border:'1px solid #1f2937', borderRadius:8, minWidth:400, maxWidth:600 }} onClick={(e)=>e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderBottom:'1px solid #1f2937' }}>
              <div style={{ fontWeight:700, fontSize:16 }}>Pair PocketPE Agent</div>
              <button className="docs-tab" onClick={()=>setShowPairingModal(false)} style={{ padding:'4px 8px' }}>×</button>
            </div>
            <div style={{ padding:16, display:'grid', gap:12 }}>
              <div style={{ color:'#9ca3af', fontSize:14 }}>
                <p>1. Open PocketPE Agent on your computer</p>
                <p>2. Go to Settings → Connection</p>
                <p>3. Enter this pairing code:</p>
              </div>
              <div style={{ 
                padding:'16px', 
                background:'#1f2937', 
                border:'1px solid #374151', 
                borderRadius:6, 
                textAlign:'center',
                fontSize:24,
                fontWeight:700,
                letterSpacing:'4px',
                color:'#e5e7eb',
                fontFamily:'monospace'
              }}>
                {pairingCode}
              </div>
              <div style={{ color:'#9ca3af', fontSize:12 }}>
                This code expires in 10 minutes. The agent will automatically pair once you enter the code.
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
                <button className="docs-tab" onClick={()=>{
                  navigator.clipboard.writeText(pairingCode).then(() => {
                    setToast({ show:true, type:'success', message:'Pairing code copied to clipboard!' });
                  }).catch(() => {});
                }}>Copy Code</button>
                <button className="docs-tab" onClick={async()=>{
                  setShowPairingModal(false);
                  // Recheck agent status after a delay
                  setTimeout(() => {
                    checkAgentStatus();
                  }, 2000);
                }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Download Agent Warning Modal */}
      {showDownloadModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:30 }} onClick={()=>setShowDownloadModal(false)}>
          <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)', background:'#0b1020', border:'1px solid #1f2937', borderRadius:8, minWidth:400, maxWidth:600 }} onClick={(e)=>e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderBottom:'1px solid #1f2937' }}>
              <div style={{ fontWeight:700, fontSize:16 }}>Download PocketPE Agent</div>
              <button className="docs-tab" onClick={()=>setShowDownloadModal(false)} style={{ padding:'4px 8px' }}>×</button>
            </div>
            <div style={{ padding:16, display:'grid', gap:16 }}>
              <div style={{ color:'#e5e7eb', fontSize:14, lineHeight:'1.6' }}>
                <p style={{ marginBottom:12 }}>
                  <strong style={{ color:'#fbbf24' }}>1.</strong> Due to computer security, our Agent app is flagged as 'suspicious'. It is required to connect your files to our server. That is its only purpose.
                </p>
                <p style={{ marginBottom:12 }}>
                  <strong style={{ color:'#fbbf24' }}>2.</strong> You will also see Microsoft Defender SmartScreen prevent the app from opening. To load, click on <strong>'More Info'</strong>, then <strong>'Run Anyway'</strong> at the bottom.
                </p>
                <p style={{ marginBottom:0 }}>
                  <strong style={{ color:'#fbbf24' }}>3.</strong> Without the Agent, you will not be able to run scripts.
                </p>
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
                <button className="docs-tab" onClick={()=>setShowDownloadModal(false)}>Cancel</button>
                <button 
                  className="docs-tab" 
                  style={{ background:'#2563eb', color:'white', border:'1px solid #2563eb' }}
                  onClick={()=>{
                    setShowDownloadModal(false);
                    // URL encode the filename to handle spaces properly
                    const filename = encodeURIComponent('PocketPE Agent Setup 1.0.0.exe');
                    window.open(`https://pocketpromptengineer.com/downloads/agent/${filename}`, '_blank');
                  }}
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Toast message={toast.message} type={toast.type} show={toast.show} onClose={() => setToast(prev => ({ ...prev, show:false }))} />
      
      {/* Prompt Generator Modal */}
      <PromptGeneratorModal
        isOpen={showPromptGeneratorModal}
        onClose={() => {
          setShowPromptGeneratorModal(false);
          setPromptLineInfo(null);
          modalOpenRef.current = false; // Reset ref so F8 can work again
        }}
        onDropIn={handlePromptDropIn}
      />
      
      {showShortcuts && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:20 }} onClick={()=>setShowShortcuts(false)}>
          <div role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()} style={{ position:'absolute', top:'10%', left:'50%', transform:'translateX(-50%)', width: 'min(900px, 92vw)', background:'#0b1020', border:'1px solid #1f2937', borderRadius:8, color:'#e5e7eb', boxShadow:'0 10px 30px rgba(0,0,0,0.4)' }}>
            <div style={{ display:'flex', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid #1f2937' }}>
              <div style={{ fontWeight:700, fontSize:16 }}>Keyboard Shortcuts</div>
              <button className="docs-tab" style={{ marginLeft:'auto' }} onClick={()=>setShowShortcuts(false)}>Close</button>
            </div>
            <div style={{ padding:16 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div>
                  <div style={{ fontWeight:600, marginBottom:8 }}>Editing</div>
                  <ul style={{ listStyle:'none', padding:0, margin:0, lineHeight:1.9 }}>
                    <li><code>Ctrl+Z</code> — Undo</li>
                    <li><code>Ctrl+Y</code> — Redo</li>
                    <li><code>Ctrl+X</code> — Cut</li>
                    <li><code>Ctrl+C</code> — Copy</li>
                    <li><code>Ctrl+V</code> — Paste</li>
                    <li><code>Ctrl+/</code> — Toggle line comment</li>
                    <li><code>Shift+Alt+A</code> — Toggle block comment</li>
                    <li><code>Ctrl+F</code> — Find</li>
                    <li><code>Ctrl+H</code> — Replace</li>
                    <li><code>Alt+↑/↓</code> — Move line up/down</li>
                    <li><code>Shift+Alt+↑/↓</code> — Copy line up/down</li>
                  </ul>
                </div>
                <div>
                  <div style={{ fontWeight:600, marginBottom:8 }}>BAIQL & Editor</div>
                  <ul style={{ listStyle:'none', padding:0, margin:0, lineHeight:1.9 }}>
                    <li><code>Ctrl+Space</code> — Suggestions</li>
                    <li><code>F12</code> — Go to definition (where supported)</li>
                    <li><code>F1</code> — BAIQL Command Reference Panel</li>
                    <li><code>Ctrl+S</code> — Save script</li>
                    <li><code>Ctrl+Shift+V</code> — Validate (use menu)
                    </li>
                    <li><code>Ctrl+Enter</code> — Execute (use menu)
                    </li>
                  </ul>
                </div>
              </div>
              <div style={{ marginTop:12, fontSize:12, color:'#9ca3af' }}>Note: Some shortcuts may vary by OS/browser. Use the menu for all actions.</div>
            </div>
          </div>
        </div>
      )}
      {showMeta && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:20 }} onClick={()=>setShowMeta(false)}>
          <div role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()} style={{ position:'absolute', top:'8%', left:'50%', transform:'translateX(-50%)', width: 'min(720px, 92vw)', background:'#0b1020', border:'1px solid #1f2937', borderRadius:8, color:'#e5e7eb', boxShadow:'0 10px 30px rgba(0,0,0,0.4)' }}>
            <div style={{ display:'flex', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid #1f2937' }}>
              <div style={{ fontWeight:700, fontSize:16 }}>Script Metadata</div>
              <button className="docs-tab" style={{ marginLeft:'auto' }} onClick={()=>setShowMeta(false)}>Close</button>
            </div>
            <div style={{ padding:16, display:'grid', gap:12 }}>
              <div style={{ display:'grid', gridTemplateColumns:'160px 1fr', alignItems:'center', gap:8 }}>
                <label>Version</label>
                <input value={meta.version} onChange={e=>setMeta(m=>({ ...m, version:e.target.value }))} placeholder="2.1.0" style={{ padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }} />
                <label>Author</label>
                <input value={meta.author} onChange={e=>setMeta(m=>({ ...m, author:e.target.value }))} placeholder="john.doe@company.com" style={{ padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }} />
                <label>Description</label>
                <input value={meta.description} onChange={e=>setMeta(m=>({ ...m, description:e.target.value }))} placeholder="Monthly customer risk scoring" style={{ padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }} />
                <label>Changelog</label>
                <textarea value={meta.changelog} onChange={e=>setMeta(m=>({ ...m, changelog:e.target.value }))} rows={3} placeholder="Updated risk thresholds per Q4 review" style={{ padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }} />
                <label>Shared with</label>
                <input value={meta.sharedWith} onChange={e=>setMeta(m=>({ ...m, sharedWith:e.target.value }))} placeholder="data_team quality_team" style={{ padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }} />
                <label>Approval required from</label>
                <input value={meta.approvalRequiredFrom} onChange={e=>setMeta(m=>({ ...m, approvalRequiredFrom:e.target.value }))} placeholder="manager" style={{ padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }} />
                <label>Inherit from</label>
                <input value={meta.inheritFrom} onChange={e=>setMeta(m=>({ ...m, inheritFrom:e.target.value }))} placeholder="base_customer_script.bql" style={{ padding:'6px 10px', background:'#0b1020', color:'#e5e7eb', border:'1px solid #1f2937', borderRadius:6 }} />
                <label>Emit as TAG</label>
                <input type="checkbox" checked={!!meta.emitMetaAsTags} onChange={e=>setMeta(m=>({ ...m, emitMetaAsTags:e.target.checked }))} />
                <label>Lock edits without approval</label>
                <input type="checkbox" checked={!!meta.lockEditsWithoutApproval} onChange={e=>setMeta(m=>({ ...m, lockEditsWithoutApproval:e.target.checked }))} />
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
                <button className="docs-tab" onClick={()=>setShowMeta(false)}>Cancel</button>
                <button className="docs-tab" onClick={async()=>{
                  try {
                    if (!scriptId) { alert('Save the script first to store metadata.'); return; }
                    await updateScriptMeta(scriptId, {
                      version: meta.version, author: meta.author, description: meta.description, changelog: meta.changelog,
                      sharedWith: meta.sharedWith, approvalRequiredFrom: meta.approvalRequiredFrom, inheritFrom: meta.inheritFrom,
                      emitMetaAsTags: !!meta.emitMetaAsTags, lockEditsWithoutApproval: !!meta.lockEditsWithoutApproval
                    });
                    setToast({ show:true, type:'success', message:'Metadata saved' });
                    setShowMeta(false);
                  } catch (e) {
                    setToast({ show:true, type:'error', message:(e && e.message) ? e.message : 'Failed to save metadata' });
                  }
                }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {screenState.show && (
        <div className="modal-backdrop" style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={(e)=>e.stopPropagation()}>
          <div className="app-modal" role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()} style={{ width:'75vw', height:'75vh', background:'#fff', color:'#111', border:'1px solid #e5e7eb', borderRadius:8, display:'flex', flexDirection:'column' }}>
            <div className="app-modal-header" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 12px', borderBottom:'1px solid #e5e7eb' }}>
              <strong>Screen Output Preview ({String(screenState.format).toUpperCase()})</strong>
              <div style={{ display:'flex', gap:8 }}>
                <button disabled={!screenState.ready || screenState.saving} onClick={handleScreenSave}>Save</button>
                <button disabled={!screenState.ready || screenState.saving} onClick={handleScreenDiscard}>Discard</button>
                <button onClick={handleScreenDiscard} title="Close">×</button>
              </div>
            </div>
            <div className="app-modal-body" style={{ padding:12, overflow:'auto', flex:1 }}>
              {(() => {
                const fmt = String(screenState.format||'txt').toLowerCase();
                if (fmt === 'json') {
                  try { const obj = JSON.parse(screenState.content||''); return (<pre style={{ margin:0, whiteSpace:'pre-wrap' }}>{JSON.stringify(obj, null, 2)}</pre>); } catch { return (<pre style={{ margin:0, whiteSpace:'pre-wrap' }}>{screenState.content}</pre>); }
                }
                if (fmt === 'csv') {
                  const rows = (screenState.content||'').split(/\r?\n/).filter(Boolean).map(r => r.split(','));
                  return (
                    <div style={{ overflow:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <tbody>
                          {rows.map((r,i)=>(<tr key={i}>{r.map((c,j)=>(<td key={j} style={{ border:'1px solid #e5e7eb', padding:'4px 6px' }}>{c}</td>))}</tr>))}
                        </tbody>
                      </table>
                    </div>
                  );
                }
                return (<pre style={{ margin:0, whiteSpace:'pre-wrap' }}>{screenState.content}</pre>);
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BAIQLEditor;

// Save & Load Modals are appended at root of this component above
// Ensure styling matches existing modal classes used across the app